import React from "react";
import { useParams, Link, useLocation, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { articles } from "./Articles";
import { freeAppsData, trialAppsData } from "./data/appsData";
import { getArticleKeywords, getArticleSeoDescription } from "./data/articleSeo";
import { buildReadMoreEnhancement, buildReadMoreFaqSchema } from "./data/readMoreEnhancements";
import articleIndexingRules from "./data/articleIndexingRules.json";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import amplemarketUserGuideVideo from "./assets/AmplemarketUserGuide.mp4";
import closeAIUserGuideVideo from "./assets/CloseAI-User-Guide.mp4";
import bitdefenderUserGuideVideo from "./assets/bitdefendervid.mp4";
import capsuleUserGuideVideo from "./assets/Capsule-User-Guide.mp4";

const NOINDEX_ARTICLE_IDS = new Set(articleIndexingRules.noindex || []);
const REDIRECTED_ARTICLE_IDS = new Set(Object.keys(articleIndexingRules.redirects || {}));

function ArticleGuideVideo({ src, ariaLabel, affiliateLink, linkLabel }) {
  if (!src || !affiliateLink) return null;
  return (
    <div style={{ textAlign: 'center', margin: '0 auto 28px', maxWidth: '800px', width: '100%' }}>
      <div
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio: '16 / 9',
          borderRadius: 16,
          overflow: 'hidden',
          background: '#0d1520',
          boxShadow: '0 4px 20px rgba(54, 255, 149, 0.2)',
        }}
      >
        <video
          src={src}
          aria-label={ariaLabel}
          controls
          playsInline
          preload="metadata"
          style={{
            width: '100%',
            height: '100%',
            display: 'block',
            objectFit: 'contain',
            background: '#000',
          }}
        />
      </div>
      <a
        href={affiliateLink}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'inline-block',
          marginTop: '16px',
          color: 'var(--accent)',
          fontWeight: 700,
          fontSize: '1.1rem',
          textDecoration: 'underline',
        }}
      >
        {linkLabel}
      </a>
    </div>
  );
}

function ShareButtons({ url, title }) {
  // Always use the article URL for sharing, never affiliate links
  const getArticleUrl = () => {
    return `${window.location.origin}/learn/${window.location.pathname.split('/').pop()}`;
  };
  const articleUrl = getArticleUrl();
  
  // Clean title for sharing - remove domain suffixes to prevent Twitter from detecting them as URLs
  const cleanTitleForSharing = (text) => {
    return text
      .replace(/VEED\.io/gi, 'VEED')
      .replace(/Reply\.io/gi, 'Reply AI')
      .replace(/Apollo\.io/gi, 'Apollo')
      .replace(/Capsule CRM/gi, 'Capsule')
      .replace(/MRPeasy/gi, 'MRPeasy')
      .replace(/Murf\.ai/gi, 'Murf AI')
      .replace(/AiSDR/gi, 'AiSDR')
      .replace(/Miro/gi, 'Miro')
      .replace(/Lindy\.ai/gi, 'Lindy AI')
      .replace(/Viral Launch/gi, 'Viral Launch')
      .replace(/Alli AI/gi, 'Alli AI')
      .replace(/AdCreative AI/gi, 'AdCreative')
      .replace(/Flowith\.io/gi, 'Flowith')
      .replace(/ScholarGPT/gi, 'ScholarGPT')
      .replace(/Brevo/gi, 'Brevo')
      // Generic patterns to catch any remaining .io, .ai, .com suffixes in product names
      .replace(/(\w+)\.io/gi, '$1')
      .replace(/(\w+)\.ai/gi, '$1 AI')
      .replace(/(\w+)\.com/gi, '$1');
  };
  const cleanTitle = cleanTitleForSharing(title);
  
  // Copy link handler with quick popup
  const handleCopy = () => {
    // Always copy the article URL, not affiliate links
    navigator.clipboard.writeText(articleUrl);
    
    // Create and show quick "Copied" popup
    const popup = document.createElement('div');
    popup.textContent = 'Copied!';
    popup.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: linear-gradient(135deg, #36ff95 0%, #00ffb2 100%);
      color: #1a1a1a;
      padding: 12px 20px;
      border-radius: 8px;
      font-weight: 600;
      font-size: 14px;
      z-index: 10000;
      box-shadow: 0 4px 16px rgba(54, 255, 149, 0.3);
      transform: translateX(100%);
      transition: transform 0.3s ease;
    `;
    
    document.body.appendChild(popup);
    
    // Animate in
    setTimeout(() => {
      popup.style.transform = 'translateX(0)';
    }, 10);
    
    // Remove after 2 seconds
    setTimeout(() => {
      popup.style.transform = 'translateX(100%)';
      setTimeout(() => {
        if (document.body.contains(popup)) {
          document.body.removeChild(popup);
        }
      }, 300);
    }, 2000);
  };
  const iconSize = 28;
  const iconStyle = { display: 'inline-block', verticalAlign: 'middle', fontSize: iconSize, width: iconSize, height: iconSize, padding: 2, cursor: 'pointer' };
  return (
    <div style={{ display: "flex", gap: 16, marginBottom: 18 }}>
      {/* Twitter */}
      <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(cleanTitle + '\n\n' + articleUrl)}&url=${encodeURIComponent(articleUrl)}`}
        target="_blank" rel="noopener noreferrer" title="Share on Twitter"
        style={{ color: "#1da1f2", ...iconStyle }}>
        <svg height={iconSize} width={iconSize} viewBox="0 0 24 24"><path fill="currentColor" d="M22.46 5.93c-.8.36-1.66.62-2.56.73a4.5 4.5 0 0 0 1.97-2.49 9.1 9.1 0 0 1-2.86 1.1A4.52 4.52 0 0 0 16.16 4a4.52 4.52 0 0 0-4.5 4.5c0 .35.04.7.1 1.02A12.82 12.82 0 0 1 3.13 4.67a4.51 4.51 0 0 0-.61 2.28c0 1.57.8 2.96 2.03 3.77a4.5 4.5 0 0 1-2.04-.56v.05c0 2.19 1.56 4.03 3.64 4.45a4.53 4.53 0 0 1-2.03.08c.57 1.78 2.23 3.09 4.2 3.12A9.06 9.06 0 0 1 2 19.54a12.79 12.79 0 0 0 6.94 2.03c8.33 0 12.89-6.89 12.89-12.89 0-.2 0-.41-.01-.61.88-.64 1.65-1.44 2.26-2.35z"/></svg>
      </a>
      {/* Facebook */}
      <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(articleUrl)}`}
        target="_blank" rel="noopener noreferrer" title="Share on Facebook"
        style={{ color: "#4267B2", ...iconStyle }}>
        <svg height={iconSize} width={iconSize} viewBox="0 0 24 24"><path fill="currentColor" d="M22.675 0h-21.35C.595 0 0 .592 0 1.326v21.348C0 23.408.595 24 1.325 24h11.495v-9.294H9.692v-3.622h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.797.143v3.24l-1.918.001c-1.504 0-1.797.715-1.797 1.763v2.313h3.587l-.467 3.622h-3.12V24h6.116C23.406 24 24 23.408 24 22.674V1.326C24 .592 23.406 0 22.675 0"/></svg>
      </a>
      {/* SMS */}
      <a href={`sms:?body=${encodeURIComponent(cleanTitle + '\n\n' + articleUrl)}`}
        title="Share via SMS" style={{ color: "#25d366", ...iconStyle }}>
        <svg height={iconSize} width={iconSize} viewBox="0 0 24 24"><path fill="currentColor" d="M20 2H4C2.897 2 2 2.897 2 4v14c0 1.103.897 2 2 2h14l4 4V4c0-1.103-.897-2-2-2zm0 15.172L18.828 16H4V4h16v13.172z"/></svg>
      </a>
      {/* Email */}
      <a href={`mailto:?subject=${encodeURIComponent(cleanTitle)}&body=${encodeURIComponent(cleanTitle + '\n\n' + articleUrl)}`}
        title="Share via Email" style={{ color: "#ea4335", ...iconStyle }}>
        <svg height={iconSize} width={iconSize} viewBox="0 0 24 24"><path fill="currentColor" d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 2v.01L12 13 4 6.01V6h16zM4 20V8.99l8 7 8-7V20H4z"/></svg>
      </a>
      {/* Copy Link */}
      <span onClick={handleCopy} title="Copy link to clipboard" style={{ color: "#36ff95", ...iconStyle }}>
        <svg height={iconSize} width={iconSize} viewBox="0 0 24 24"><path fill="currentColor" d="M3.9 12c0-2.25 1.85-4.1 4.1-4.1h4V6.5h-4C5.01 6.5 3 8.51 3 11s2.01 4.5 4.5 4.5h4v-1.4h-4c-2.25 0-4.1-1.85-4.1-4.1zm5.6 1.4h5V10.6h-5v2.8zm7.5-7.5h-4v1.4h4c2.25 0 4.1 1.85 4.1 4.1s-1.85 4.1-4.1 4.1h-4v1.4h4c2.99 0 5-2.01 5-4.5s-2.01-4.5-5-4.5z"/></svg>
      </span>
    </div>
  );
}

// Function to find related articles based on keywords, title, and content similarity
function findRelatedArticles(currentArticle, allArticles, limit = 2) {
  if (!currentArticle || !allArticles || allArticles.length === 0) return [];
  
  // Extract keywords from title and preview
  const currentTitle = (currentArticle.title || '').toLowerCase();
  const currentPreview = (currentArticle.preview || '').toLowerCase();
  const currentContent = (currentArticle.content || '').toLowerCase();
  
  // Create a set of keywords from title (split by common words)
  const stopWords = new Set(['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'is', 'are', 'was', 'were', 'be', 'been', 'being', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'should', 'could', 'may', 'might', 'must', 'can', 'this', 'that', 'these', 'those', 'i', 'you', 'he', 'she', 'it', 'we', 'they', 'what', 'which', 'who', 'whom', 'whose', 'where', 'when', 'why', 'how', 'all', 'each', 'every', 'both', 'few', 'more', 'most', 'other', 'some', 'such', 'no', 'nor', 'not', 'only', 'own', 'same', 'so', 'than', 'too', 'very', 's', 't', 'can', 'will', 'just', 'don', 'should', 'now']);
  
  const extractKeywords = (text) => {
    return text
      .replace(/[^\w\s]/g, ' ')
      .split(/\s+/)
      .filter(word => word.length > 3 && !stopWords.has(word.toLowerCase()))
      .map(word => word.toLowerCase());
  };
  
  const currentKeywords = new Set([
    ...extractKeywords(currentTitle),
    ...extractKeywords(currentPreview),
    ...extractKeywords(currentContent.substring(0, 1000)) // First 1000 chars for performance
  ]);
  
  // Topic clusters - group related articles
  const topicClusters = {
    'ai-content': ['ai', 'content', 'writing', 'copy', 'text', 'article', 'blog', 'seo'],
    'ai-video': ['video', 'editing', 'production', 'youtube', 'streaming', 'media'],
    'ai-audio': ['audio', 'voice', 'podcast', 'music', 'sound', 'speech'],
    'ai-marketing': ['marketing', 'advertising', 'campaign', 'social', 'email', 'outreach'],
    'ai-sales': ['sales', 'crm', 'leads', 'prospects', 'contacts', 'outreach'],
    'ai-design': ['design', 'graphic', 'image', 'visual', 'creative', 'art'],
    'ai-productivity': ['productivity', 'automation', 'workflow', 'task', 'calendar', 'time'],
    'ai-seo': ['seo', 'search', 'keyword', 'ranking', 'optimization', 'serp'],
    'ai-analytics': ['analytics', 'data', 'metrics', 'dashboard', 'reporting', 'insights'],
    'ai-chatbot': ['chatbot', 'chat', 'conversation', 'messaging', 'support', 'assistant'],
    'ai-ecommerce': ['ecommerce', 'amazon', 'seller', 'product', 'inventory', 'fba'],
    'ai-hosting': ['hosting', 'server', 'infrastructure', 'deployment', 'cloud'],
  };
  
  // Score articles based on keyword overlap and topic clusters
  const seenIds = new Set(); // Track article IDs to prevent duplicates
  const scoredArticles = allArticles
    .filter(a => {
      // Filter out current article, articles without title/preview, and duplicates
      if (!a || !a.id || a.id === currentArticle.id || !a.title || !a.preview) return false;
      if (NOINDEX_ARTICLE_IDS.has(a.id) || REDIRECTED_ARTICLE_IDS.has(a.id)) return false;
      if (seenIds.has(a.id)) return false; // Skip duplicates
      seenIds.add(a.id);
      return true;
    })
    .map(otherArticle => {
      const otherTitle = (otherArticle.title || '').toLowerCase();
      const otherPreview = (otherArticle.preview || '').toLowerCase();
      const otherKeywords = new Set([
        ...extractKeywords(otherTitle),
        ...extractKeywords(otherPreview)
      ]);
      
      // Calculate keyword overlap
      let score = 0;
      currentKeywords.forEach(keyword => {
        if (otherKeywords.has(keyword)) {
          score += 2; // Title/preview matches are weighted higher
        }
      });
      
      // Check topic cluster matches
      Object.entries(topicClusters).forEach(([cluster, keywords]) => {
        const currentInCluster = keywords.some(kw => currentKeywords.has(kw));
        const otherInCluster = keywords.some(kw => otherKeywords.has(kw));
        if (currentInCluster && otherInCluster) {
          score += 3; // Topic cluster matches get bonus points
        }
      });
      
      // Bonus for same level if it exists
      if (currentArticle.level && otherArticle.level && currentArticle.level === otherArticle.level) {
        score += 1;
      }
      
      return { article: otherArticle, score };
    })
    .filter(item => item.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(item => item.article);
  
  // Final deduplication by ID to ensure no duplicates
  const uniqueArticles = [];
  const finalSeenIds = new Set();
  for (const article of scoredArticles) {
    if (article && article.id && !finalSeenIds.has(article.id)) {
      finalSeenIds.add(article.id);
      uniqueArticles.push(article);
      if (uniqueArticles.length >= limit) break;
    }
  }
  
  return uniqueArticles;
}

// Function to add internal links to article content
// This creates contextual links to related articles mentioned in the content
function addInternalLinks(content, currentArticleId, allArticles) {
  if (!content || typeof content !== 'string' || !allArticles || !Array.isArray(allArticles)) return content;
  
  // Blacklist of common words that should NEVER be auto-linked
  // This includes common English words, technical terms, and generic descriptors
  const blacklistedWords = new Set([
    // Articles and common words
    'the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by',
    'from', 'up', 'about', 'into', 'through', 'during', 'including', 'until', 'against', 'among',
    'over', 'under', 'above', 'below', 'between', 'within', 'without', 'across', 'around', 'behind',
    'beside', 'beyond', 'near', 'inside', 'outside', 'toward', 'towards', 'upon', 'via', 'per',
    
    // Common verbs
    'is', 'are', 'was', 'were', 'be', 'been', 'being', 'have', 'has', 'had', 'do', 'does', 'did',
    'will', 'would', 'should', 'could', 'may', 'might', 'must', 'can', 'shall', 'ought',
    'get', 'got', 'give', 'gave', 'take', 'took', 'make', 'made', 'go', 'went', 'come', 'came',
    'see', 'saw', 'know', 'knew', 'think', 'thought', 'say', 'said', 'tell', 'told', 'ask', 'asked',
    'work', 'worked', 'use', 'used', 'try', 'tried', 'call', 'called', 'find', 'found', 'want', 'wanted',
    'need', 'needed', 'help', 'helped', 'seem', 'seemed', 'feel', 'felt', 'become', 'became', 'leave', 'left',
    'put', 'set', 'add', 'remove', 'update', 'change', 'modify', 'create', 'edit', 'delete', 'save', 'load',
    'manage', 'control', 'access', 'start', 'stop', 'run', 'ran', 'show', 'showed', 'hide', 'hid',
    'read', 'read', 'write', 'wrote', 'send', 'sent', 'receive', 'received', 'copy', 'copied', 'paste', 'pasted',
    'cut', 'search', 'filter', 'sort', 'view', 'click', 'link', 'open', 'opened', 'close', 'closed',
    
    // Common nouns
    'time', 'year', 'people', 'way', 'day', 'man', 'thing', 'woman', 'life', 'child', 'world', 'school',
    'state', 'family', 'student', 'group', 'country', 'problem', 'hand', 'part', 'place', 'case', 'week',
    'company', 'system', 'program', 'question', 'work', 'government', 'number', 'night', 'point', 'home',
    'water', 'room', 'mother', 'area', 'money', 'story', 'fact', 'month', 'lot', 'right', 'study', 'book',
    'eye', 'job', 'word', 'business', 'issue', 'side', 'kind', 'head', 'house', 'service', 'friend', 'father',
    'power', 'hour', 'game', 'line', 'end', 'member', 'law', 'car', 'city', 'community', 'name', 'president',
    'team', 'minute', 'idea', 'kid', 'body', 'information', 'back', 'parent', 'face', 'others', 'level',
    'office', 'door', 'health', 'person', 'art', 'war', 'history', 'party', 'result', 'change', 'morning',
    'reason', 'research', 'girl', 'guy', 'moment', 'air', 'teacher', 'force', 'education', 'robot', 'small',
    
    // Technical/common terms
    'complete', 'guide', 'review', 'platform', 'tool', 'system', 'powered', 'ai-powered', 'ai',
    'voice', 'video', 'audio', 'image', 'text', 'data', 'file', 'page', 'site', 'web', 'app',
    'user', 'team', 'service', 'product', 'feature', 'function', 'option', 'button', 'menu',
    'support', 'assist', 'tutorial', 'documentation', 'learn', 'learning', 'learned',
    
    // Adjectives and descriptors
    'good', 'bad', 'new', 'old', 'first', 'last', 'long', 'great', 'little', 'own', 'other', 'right',
    'big', 'high', 'small', 'large', 'next', 'early', 'young', 'important', 'few', 'public', 'bad',
    'same', 'able', 'human', 'local', 'late', 'hard', 'major', 'better', 'economic', 'strong', 'possible',
    'whole', 'free', 'military', 'true', 'federal', 'international', 'full', 'special', 'easy', 'clear',
    'recent', 'certain', 'personal', 'open', 'red', 'difficult', 'available', 'likely', 'national', 'real',
    'best', 'better', 'big', 'black', 'certain', 'clear', 'close', 'cold', 'common', 'complete',
    'dark', 'dead', 'dear', 'deep', 'different', 'difficult', 'dirty', 'dry', 'early', 'easy', 'empty',
    'equal', 'exact', 'extra', 'fair', 'false', 'familiar', 'far', 'fast', 'fat', 'fine', 'firm',
    'first', 'fit', 'flat', 'foreign', 'free', 'fresh', 'full', 'funny', 'general', 'gentle', 'glad',
    'good', 'great', 'green', 'half', 'handsome', 'happy', 'hard', 'harsh', 'healthy', 'heavy', 'high',
    'holy', 'honest', 'hot', 'huge', 'human', 'hungry', 'ill', 'important', 'impossible', 'inner', 'intelligent',
    'interesting', 'international', 'kind', 'large', 'last', 'late', 'lazy', 'left', 'light', 'like', 'little',
    'live', 'local', 'lonely', 'long', 'loose', 'loud', 'lovely', 'low', 'lucky', 'mad', 'main', 'major',
    'many', 'married', 'material', 'medical', 'mental', 'middle', 'military', 'minor', 'modern', 'moral',
    'most', 'much', 'musical', 'narrow', 'national', 'natural', 'near', 'necessary', 'negative', 'nervous',
    'new', 'nice', 'noble', 'normal', 'northern', 'notable', 'novel', 'numerous', 'obvious', 'odd', 'official',
    'old', 'only', 'open', 'opposite', 'orange', 'ordinary', 'original', 'other', 'outer', 'overall', 'own',
    'particular', 'past', 'patient', 'peaceful', 'perfect', 'personal', 'physical', 'plain', 'pleasant', 'plenty',
    'political', 'poor', 'popular', 'positive', 'possible', 'potential', 'powerful', 'practical', 'precious',
    'present', 'pretty', 'previous', 'primary', 'prime', 'private', 'probable', 'professional', 'proper',
    'proud', 'public', 'pure', 'quick', 'quiet', 'quite', 'rapid', 'rare', 'raw', 'ready', 'real', 'recent',
    'red', 'regular', 'relative', 'relevant', 'religious', 'remarkable', 'remote', 'responsible', 'rich',
    'right', 'rough', 'round', 'royal', 'rude', 'rural', 'sad', 'safe', 'same', 'satisfied', 'scared',
    'scientific', 'second', 'secret', 'secure', 'serious', 'several', 'severe', 'sharp', 'short', 'shy',
    'sick', 'significant', 'silent', 'silly', 'similar', 'simple', 'single', 'skilled', 'sleepy', 'slow',
    'small', 'smart', 'smooth', 'social', 'soft', 'solid', 'some', 'sorry', 'southern', 'spare', 'special',
    'specific', 'spiritual', 'splendid', 'square', 'standard', 'steady', 'steep', 'still', 'straight', 'strange',
    'strict', 'strong', 'stupid', 'subject', 'substantial', 'successful', 'sudden', 'sufficient', 'suitable',
    'sure', 'surprised', 'sweet', 'swift', 'tall', 'tame', 'tart', 'tasteful', 'tasty', 'teachable', 'tedious',
    'teenage', 'temporary', 'tender', 'tense', 'terrible', 'thankful', 'thick', 'thin', 'thirsty', 'thorough',
    'thoughtful', 'tight', 'tiny', 'tired', 'tough', 'tragic', 'tremendous', 'trivial', 'troublesome', 'true',
    'trustful', 'trusting', 'trustworthy', 'ugly', 'ultimate', 'unable', 'unaware', 'uncertain', 'uncommon',
    'unconscious', 'under', 'uneasy', 'unexpected', 'unfair', 'unfortunate', 'unhappy', 'unhealthy', 'uniform',
    'unique', 'universal', 'unknown', 'unlikely', 'unlucky', 'unnatural', 'unpleasant', 'unreasonable', 'unsafe',
    'unsatisfied', 'unusual', 'unwilling', 'upper', 'upset', 'urban', 'urgent', 'useful', 'useless', 'usual',
    'vague', 'vain', 'valid', 'valuable', 'variable', 'vast', 'verbal', 'vertical', 'very', 'vicious', 'victorious',
    'violent', 'virtual', 'visible', 'visual', 'vital', 'vivid', 'vocal', 'void', 'voluntary', 'vulnerable',
    'warm', 'wary', 'wasteful', 'watchful', 'watery', 'weak', 'wealthy', 'weary', 'weird', 'welcome', 'well',
    'western', 'wet', 'whole', 'wicked', 'wide', 'widespread', 'wild', 'willing', 'wise', 'witty', 'wonderful',
    'wooden', 'woolly', 'worried', 'worse', 'worst', 'worth', 'worthy', 'wounded', 'wrong', 'young', 'youthful',
    'zealous', 'zero'
  ]);
  
  // Create a map of distinctive product/tool names to article IDs
  // Only use actual product names from article IDs, not common words from titles
  const productNameMap = new Map();
  
  allArticles.forEach(article => {
    if (!article || !article.id || typeof article.id !== 'string' || article.id === currentArticleId) return;
    
    // Extract product name ONLY from article ID (most reliable source)
    // IDs are usually like "adcreative-ai", "murf-ai-complete-guide", "lindy-ai", etc.
    const idParts = article.id.split('-');
    
    // Find the first substantial part that's not a blacklisted word
    // Must be at least 4 characters and not in blacklist
    const productName = idParts.find(part => {
      if (!part || part.length < 4) return false;
      const lowerPart = part.toLowerCase();
      // CRITICAL: Check blacklist FIRST before any other checks
      if (blacklistedWords.has(lowerPart)) return false;
      // Additional check: skip common suffixes/prefixes
      if (['complete', 'guide', 'review', 'platform', 'tool', 'system', 'ai', 'io', 'com', 'the', 'a', 'an'].includes(lowerPart)) return false;
      return true;
    });
    
    // Only add to map if it's a valid product name (not a common word)
    // Triple-check blacklist before adding - this is critical
    if (productName && 
        typeof productName === 'string' && 
        productName.length >= 4) {
      const lowerProductName = productName.toLowerCase();
      // Final blacklist check - if it's a common word, skip it entirely
      // Also check for common word variations
      if (!blacklistedWords.has(lowerProductName) && 
          !blacklistedWords.has(lowerProductName + 's') && // plural
          !blacklistedWords.has(lowerProductName + 'ing') && // gerund
          !blacklistedWords.has(lowerProductName + 'ed')) { // past tense
        productNameMap.set(lowerProductName, article);
      }
    }
  });
  
  let processedContent = content;
  const linkedArticles = new Set(); // Track which articles we've linked to avoid over-linking
  
  // Process each potential product mention
  productNameMap.forEach((targetArticle, productName) => {
    if (!targetArticle || !targetArticle.id || linkedArticles.has(targetArticle.id)) return; // Already linked this article
    if (NOINDEX_ARTICLE_IDS.has(targetArticle.id) || REDIRECTED_ARTICLE_IDS.has(targetArticle.id)) return;
    if (!productName || typeof productName !== 'string') return;
    
    // CRITICAL: Double-check blacklist - never link common words
    // This MUST be checked before any regex matching
    const lowerProductName = productName.toLowerCase();
    if (blacklistedWords.has(lowerProductName)) {
      return; // Skip this entirely - it's a common word
    }
    
    // Create regex to find product mentions, but avoid:
    // - Already linked text
    // - Inside code blocks (```)
    // - Inside HTML tags
    // - Inside existing markdown links
    
    const escapedName = productName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // Build regex pattern as a string to avoid template literal issues with backticks
    // Pattern: match product name, but not if it's inside code blocks, HTML tags, or existing links
    // Using String.fromCharCode(96) for backtick to avoid template literal parsing issues
    const backtick = String.fromCharCode(96);
    const regexPattern = '(^|[^\\[\\]()' + backtick + '])(\\b' + escapedName + '\\b)(?![^<]*>)(?![^\\[]*\\]\\()';
    const regex = new RegExp(regexPattern, 'gi');
    
    // Check if product is mentioned in content
    const matches = [...processedContent.matchAll(regex)];
    if (matches.length === 0) return;
    
    // Only link the first 1-2 mentions to avoid over-linking
    let linkCount = 0;
    const maxLinks = 2;
    
    // Use a more reliable approach: find all matches first, then process them in reverse
    const allMatches = [];
    let matchResult;
    const tempRegex = new RegExp(regexPattern, 'gi');
    while ((matchResult = tempRegex.exec(processedContent)) !== null) {
      allMatches.push({
        index: matchResult.index,
        match: matchResult[0],
        prefix: matchResult[1],
        term: matchResult[2]
      });
    }
    
    // Process matches in reverse order to maintain indices when replacing
    for (let i = allMatches.length - 1; i >= 0 && linkCount < maxLinks; i--) {
      const { index, match, prefix, term } = allMatches[i];
      
      // Check if we're inside a code block
      const beforeMatch = processedContent.substring(0, index);
      const codeBlockCount = (beforeMatch.match(/```/g) || []).length;
      if (codeBlockCount % 2 !== 0) continue; // Inside code block
      
      // Check if we're inside an HTML tag
      const htmlTagPattern = /<[^>]*>/g;
      const htmlTags = [...beforeMatch.matchAll(htmlTagPattern)];
      const lastTag = htmlTags[htmlTags.length - 1];
      if (lastTag && lastTag[0].startsWith('<') && !lastTag[0].includes('/')) {
        const tagName = lastTag[0].match(/<(\w+)/);
        if (tagName) {
          const closingTag = new RegExp(`</${tagName[1]}>`, 'i');
          const afterMatch = processedContent.substring(index + match.length);
          if (!closingTag.test(afterMatch.substring(0, 500))) {
            continue; // Inside unclosed HTML tag
          }
        }
      }
      
      // Check if already in a markdown link - look backwards for [ and forwards for ](
      const lookBack = 500;
      const lookForward = 500;
      const contextStart = Math.max(0, index - lookBack);
      const contextEnd = Math.min(processedContent.length, index + match.length + lookForward);
      const context = processedContent.substring(contextStart, contextEnd);
      const relativeIndex = index - contextStart;
      
      // Find the nearest [ before our match
      const beforeContext = context.substring(0, relativeIndex);
      const lastOpenBracket = beforeContext.lastIndexOf('[');
      
      if (lastOpenBracket !== -1) {
        // Check if there's a ]( after the [ and before/at our match
        const afterOpenBracket = context.substring(lastOpenBracket);
        const closeBracketIndex = afterOpenBracket.indexOf('](');
        
        if (closeBracketIndex !== -1) {
          // Check if our match is between [ and ](
          const linkTextStart = lastOpenBracket;
          const linkTextEnd = lastOpenBracket + closeBracketIndex;
          if (relativeIndex >= linkTextStart && relativeIndex + match.length <= linkTextEnd) {
            continue; // Already inside a markdown link text
          }
        }
      }
      
      // FINAL SAFETY CHECK: Make absolutely sure the matched term isn't a blacklisted word
      const matchedTerm = term.toLowerCase();
      if (blacklistedWords.has(matchedTerm)) {
        continue; // Skip this match - it's a common word
      }
      
      linkCount++;
      linkedArticles.add(targetArticle.id);
      
      // Replace with markdown link (processing in reverse so indices stay valid)
      const replacement = `${prefix}[${term}](/learn/${targetArticle.id})`;
      processedContent = processedContent.substring(0, index) + replacement + processedContent.substring(index + match.length);
    }
  });
  
  return processedContent;
}
  
export default function ArticlePage() {
  const { id } = useParams();
  const location = useLocation();
  const redirectTarget = articleIndexingRules.redirects?.[id];
  const isNoindexArticle = (articleIndexingRules.noindex || []).includes(id);

  if (redirectTarget) {
    return <Navigate to={`/learn/${redirectTarget}`} replace />;
  }
  
  // Safety check for articles array
  if (!articles || !Array.isArray(articles)) {
    console.error('Articles array is not properly loaded');
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <h2>Error Loading Articles</h2>
        <p>Please refresh the page or contact support if the issue persists.</p>
      </div>
    );
  }
  
  const article = articles.find(a => a.id === id);
  const fromPage = location.state?.from || (location.pathname.startsWith('/learn') ? '/learn' : '/');
  
  // Extract video HTML from content if present
  const extractVideoFromContent = (content) => {
    if (!content || typeof content !== 'string') return { video: null, restContent: content };
    
    // Check if content contains YouTube embed
    if (!content.includes('youtube.com/embed')) {
      return { video: null, restContent: content };
    }
    
    // More aggressive pattern: match from start, including all whitespace, the entire div block
    // This pattern handles multiline HTML with proper closing tags
    const videoPattern = /^(\s*<div[^>]*>[\s\S]*?<\/iframe>[\s\S]*?<\/div>\s*)/;
    const videoMatch = content.match(videoPattern);
    
    if (videoMatch) {
      const videoHtml = videoMatch[1].trim();
      // Remove the matched video block from content
      let restContent = content.replace(videoPattern, '').trim();
      // Aggressively remove ANY remaining video HTML (in case regex didn't catch it all)
      restContent = restContent.replace(/<div[^>]*>[\s\S]*?youtube\.com\/embed[\s\S]*?<\/div>/gi, '').trim();
      // Also remove any standalone iframe tags
      restContent = restContent.replace(/<iframe[^>]*youtube\.com\/embed[^>]*>[\s\S]*?<\/iframe>/gi, '').trim();
      
      return {
        video: videoHtml,
        restContent: restContent
      };
    }
    
    // Fallback: if no match but video exists, try to remove it anyway
    const fallbackCleaned = content.replace(/<div[^>]*>[\s\S]*?youtube\.com\/embed[\s\S]*?<\/div>/gi, '').trim();
    return { video: null, restContent: fallbackCleaned };
  };
  
  // Function to generate heading ID from text
  const generateHeadingId = (text) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const ScholarGPTHeading = ({ level, children, ...props }) => {
    // Extract text content from children, handling both strings and React elements
    const extractTextFromChildren = (children) => {
      if (typeof children === 'string') {
        return children;
      }
      if (Array.isArray(children)) {
        return children.map(child => {
          if (typeof child === 'string') {
            return child;
          }
          if (child && typeof child === 'object' && child.props && child.props.children) {
            return extractTextFromChildren(child.props.children);
          }
          return '';
        }).join('');
      }
      if (children && typeof children === 'object' && children.props && children.props.children) {
        return extractTextFromChildren(children.props.children);
      }
      return '';
    };
    
    const headingText = extractTextFromChildren(children);
    const headingId = generateHeadingId(headingText);
    
    if (
      article.id === 'scholar-gpt' &&
      level === 2 &&
      typeof children[0] === 'string' &&
      children[0].trim().toLowerCase() === 'how it works'
    ) {
      return (
        <>
          <h2 {...props} id={headingId} className="article-md-heading" style={{ margin: '32px 0 12px 0', fontSize: '1.4rem', letterSpacing: 0.1 }}>{children}</h2>
          <img
            src="/scholargpt.jpg"
            alt="Scholar GPT"
            style={{
              width: '100%',
              maxWidth: '420px',
              display: 'block',
              margin: '24px auto',
              borderRadius: 16
            }}
          />
        </>
      );
    }
    const Tag = `h${level}`;
    return <Tag {...props} id={headingId} className="article-md-heading" style={{ margin: '32px 0 12px 0', fontSize: '1.4rem', letterSpacing: 0.1 }}>{children}</Tag>;
  };

  if (!article) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <h2>BetterAiBots Article Not Found</h2>
        <Link to={fromPage} style={{ color: "#0bbfdb" }}>Back to {fromPage === '/apps' ? 'Apps' : 'Learn'}</Link>
      </div>
    );
  }

      const pageUrl = `${window.location.origin}/learn/${article.id}`;
      // Always use the article URL for sharing, not affiliate links
      const shareUrl = pageUrl;
  const appCatalog = [...freeAppsData, ...trialAppsData];
  const articleApp = appCatalog.find((app) => app.readMoreLink === `/learn/${article.id}`) || null;
  const readMoreEnhancement = buildReadMoreEnhancement(article, articleApp);
  const readMoreFaqSchema = buildReadMoreFaqSchema(article, articleApp);
  const articleBreadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://betteraibots.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Learn",
        "item": "https://betteraibots.com/learn"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": article.title,
        "item": `https://betteraibots.com/learn/${article.id}`
      }
    ]
  };

  // Process content to add internal links
  const processedContent = article ? addInternalLinks(article.content, article.id, articles) : null;
  const { video, restContent } = article ? extractVideoFromContent(processedContent) : { video: null, restContent: null };
  const baseArticleMarkdownContent = restContent ?? processedContent ?? article?.content ?? '';
  const articleMarkdownContent = `${baseArticleMarkdownContent}${readMoreEnhancement.markdown || ''}`.trim();
  const images = article.images || [];
  // Prioritize article cover image (used for paid apps), fallback to images array
  const primaryImage = article.cover || images[0];
  // Get secondary images (all images except the first one, or all if using cover)
  // When using cover, exclude the cover image from secondary images if it's also in the images array
  const secondaryImages = article.cover 
    ? images.filter(img => img !== article.cover && img !== images[0])
    : images.slice(1);
  
  // Mapping of article IDs to their affiliate links
  const affiliateLinks = {
    "bebop-ai": "https://try.bebop.ai/o004cood3aod",
    "pipes-ai": "https://try.pipes.ai/hmqj0m3am6un",
    "runpod": "https://get.runpod.io/w3na2cm4xdjp",
    "seosparkplug": "https://seosparkplug.com",
    "adwisely": "https://get.adwisely.com/BAIB",
    "brevo-complete-guide": "https://get.brevo.com/um9xszmf3nfd",
    "aisdr-complete-guide": "https://partner.aisdr.com/5fxea2u5m85d",
    "adcreative-ai": "https://free-trial.adcreative.ai/BAIB",
    "activecampaign": "https://try.activecampaign.com/q84v43xjanzq",
    "alli-ai": "https://try.alliai.com/0guepbqpqhsf",
    "apollo-io": "https://get.apollo.io/BAIB",
    "atria": "https://affiliates.tryatria.com/BAIB",
    "beautiful-ai-complete-guide": "https://beautifulai.partnerlinks.io/mf21oqg4umdr",
    "recomaze": "https://affiliate.recomaze.ai/BAIB",
    "vida-ai-agent-os": "https://try.vida.io/BAIB",
    "vista-social": "https://join.vistasocial.com/BAIB",
    "plesk": "https://try.plesk.com/BAIB",
    "surecam": "https://join.surecam.com/BAIB",
    "diginius": "https://get.diginius.com/rvao67pwqqux",
    "smartli": "https://smartli.partnerlinks.io/BAIB",
    "consensus": "https://get.consensus.app/BAIB",
    "browse-ai": "https://partners.browse.ai/BAIB",
    "blackbox-ai": "https://blackboxai.partnerlinks.io/BAIB",
    "capsule-crm-complete-guide": "https://get.capsulenow.io/BAIB",
    "close-complete-guide": "https://refer.close.com/x04k0vvgqte4",
    "evolve-complete-guide": "https://go.evolveplatform.ai/9lwvqqq20p28",
    "alidrop-complete-guide": "https://get.alidrop.co/a1gll4u5mi12",
    "flowith-io": "https://aff.flowith.io/52dtlja1b580",
    "hume-ai": "https://try.hume.ai/BAIB",
    "practice-ignition": "https://get.practiceignition.com/BAIB",
    "omniseo": "https://get.omniseo.com/BAIB",
    "compliancely": "https://get.compliancely.com/gwmdzyd8i95g",
    "teikametrics": "https://join.teikametrics.com/BAIB",
    "spiky-ai": "https://get.spiky.ai/BAIB",
    "emergent": "https://get.emergent.sh/BAIB",
    "sleekflow": "https://sleekflow.partnerlinks.io/BAIB",
    "treble-ai": "https://trebleai.partnerlinks.io/91f6vx0zwova",
    "invideo-ai": "https://invideo.sjv.io/c/6368097/2210623/12258",
    "lindy-ai": "https://try.lindy.ai/lhgvxfidor04",
    "lusha": "https://partnerstack.lusha.com/w61xn76pa3sr",
    "meetgeek-complete-guide": "https://get.meetgeek.ai/BAIB",
    "miro-complete-guide": "https://ps.miro-affiliate.com/gwnvu4zj3r8r",
    "mrpeasy-complete-guide": "https://try.mrpeasy.com/m72w6bztymwh",
    "murf-ai-complete-guide": "https://get.murf.ai/i5n7gfvz5cbw",
    "reply-io-complete-guide": "https://get.reply.io/ub7edypmq2gj",
    "thordata": "https://affiliate.thordata.com/BAIB",
    "tidio-ai": "https://affiliate.tidio.com/BAIB",
    "veed-complete-guide": "https://veed.cello.so/2KWVFnsJmFA",
    "webydo": "https://partners.webydo.com/BAIB",
    "wispr-flow-complete-guide": "https://wisprflow.ai/downloads?referral=KING16",
    "catalister": "https://join.catalister.com/BAIB",
    "castmagic": "https://get.castmagic.io/qdu0jfhfcurv",
    "viral-launch": "https://affiliate.viral-launch.com/BAIB",
    "anybiz": "https://anybiz.io/?fpr=ai4n56",
    "megahr": "https://try.megahr.com/BAIB",
    "airia": "https://try.airia.com/BAIB",
    "museit-art-complete-guide": "https://Museit.art",
  "nanoz-complete-guide": "https://nanoz.fun",
  "netlify-complete-guide": "https://join.netlify.com/BAIB",
  "pinecone-vector-database": "https://www.skowers.com/api/go/d8",
  "prezi-complete-guide": "https://try.prezi.com/9kk83fjh4yri",
    "printify-complete-guide": "https://try.printify.com/BAIB",
    "quillbot-complete-guide": "https://try.quillbot.com/BAIB",
    "landingi-complete-guide": "https://try.landingi.com/lwfc597yjm25",
    "tradify-complete-guide": "https://partners.tradifyhq.com/rh188vnbjrvr",
    "trainual-complete-guide": "https://start.trainual.com/7epsexyi89t3",
    "logome-complete-guide": "https://logomeai.partnerlinks.io/BAIB",
    "datahawk": "https://get.datahawk.co/eoe07kko8pol",
    "descript": "https://get.descript.com/BAIB",
    "elevate-forward": "https://partners.elevateforward.ai/iy1986n6gbz4",
    "unbounce": "https://unbounce.partnerlinks.io/BAIB",
    "landbot": "https://get.landbotlab.com/BAIB",
    "reclaim-ai": "https://go.reclaim.ai/BAIB",
    "amplemarket": "https://grow.amplemarket.com/BAIB",
    "mindstudio": "https://get.mindstudio.ai/BAIB",
    "elevenlabs": "https://try.elevenlabs.io/BAIB",
    "gamma": "https://try.gamma.app/BAIB",
    "rankprompt": "https://join.rankprompt.com/BAIB",
    "snowfire-ai": "https://partners.snowfire.ai/8f5vtlj0mksq",
    "volza": "https://partner.volza.com/BAIB",
    "bidx": "https://try.bidx.io/BAIB",
    "bitdefender-complete-guide": "https://get.bitdefender.com/BAIB",
    "rebolt-complete-guide": "https://join.rebolthq.com/BAIB",
    "calilio-complete-guide": "https://try.calilio.com/BAIB",
    "laxis": "https://get.laxis.com/BAIB",
    "krispcall": "https://try.krispcall.com/BAIB",
    "getresponse": "https://try.getresponsetoday.com/BAIB",
    "gohighlevel": "https://www.gohighlevel.com/?fp_ref=baib",
    "weave": "https://partnerstack.getweave.com/BAIB",
    "turbotic": "https://try.turbotic.com/BAIB",
    "creativescore": "https://affiliate.creativescore.ai/BAIB",
    "seamless-ai-complete-guide": "https://get.seamless.ai/BAIB",
    "instaglamor": "https://www.InstaGlamor.com"
  };
  
  const affiliateLink = affiliateLinks[article.id] || null;
  
  // Function to split content roughly in half for inserting secondary images
  const splitContentForSecondaryImages = (content) => {
    if (!content || secondaryImages.length === 0) {
      return { firstHalf: content, secondHalf: '' };
    }
    
    // Split by double newlines (paragraphs)
    const paragraphs = content.split(/\n\n+/);
    const midPoint = Math.floor(paragraphs.length / 2);
    
    // Try to find a heading near the midpoint for a cleaner split
    let splitIndex = midPoint;
    for (let i = midPoint; i < paragraphs.length && i < midPoint + 3; i++) {
      if (paragraphs[i].trim().startsWith('##')) {
        splitIndex = i;
        break;
      }
    }
    
    const firstHalf = paragraphs.slice(0, splitIndex).join('\n\n');
    const secondHalf = paragraphs.slice(splitIndex).join('\n\n');
    
    return { firstHalf, secondHalf };
  };
  
  const { firstHalf, secondHalf } = splitContentForSecondaryImages(articleMarkdownContent);

  // Shared markdown components for rendering article content
  const markdownComponents = {
    h2: ScholarGPTHeading,
    h3: ({ node, children, ...props }) => {
      const extractTextFromChildren = (children) => {
        if (typeof children === 'string') {
          return children;
        }
        if (Array.isArray(children)) {
          return children.map(child => {
            if (typeof child === 'string') {
              return child;
            }
            if (child && typeof child === 'object' && child.props && child.props.children) {
              return extractTextFromChildren(child.props.children);
            }
            return '';
          }).join('');
        }
        if (children && typeof children === 'object' && children.props && children.props.children) {
          return extractTextFromChildren(children.props.children);
        }
        return '';
      };
      const headingText = extractTextFromChildren(children);
      const headingId = generateHeadingId(headingText);
      return (
        <h3 {...props} id={headingId} className="article-md-heading" style={{ margin: '28px 0 10px 0', fontSize: '1.13rem', letterSpacing: 0.1 }}>{children}</h3>
      );
    },
    p: ({ children }) => <p>{children}</p>,
    strong: ({ children, ...props }) => highlightSpeakers(children),
    ul: ({ children, ...props }) => (
      <ul style={{ paddingLeft: 20, margin: '16px 0' }}>{children}</ul>
    ),
    ol: ({ children, ...props }) => (
      <ol style={{ paddingLeft: 20, margin: '16px 0' }}>{children}</ol>
    ),
    li: ({ children, ...props }) => (
      <li style={{ marginBottom: 8, display: 'block' }}>{children}</li>
    ),
    img: () => null,
    a: ({ href, children, ...props }) => {
      if (href && href.startsWith('#')) {
        return (
          <a
            href={href}
            onClick={(e) => {
              e.preventDefault();
              const targetId = href.substring(1);
              const targetElement = document.getElementById(targetId);
              if (targetElement) {
                targetElement.scrollIntoView({ 
                  behavior: 'auto',
                  block: 'start'
                });
              }
            }}
            style={{ color: 'var(--accent)', textDecoration: 'underline', cursor: 'pointer' }}
            {...props}
          >
            {children}
          </a>
        );
      }
      return <a href={href} style={{ color: 'var(--accent)', textDecoration: 'underline' }} {...props}>{children}</a>;
    }
  };

  // Utility to highlight speakers in any strong/bold text
  const highlightSpeakers = (children) => {
    if (!children) return children;
    return React.Children.map(children, child => {
      if (
        typeof child === "string" &&
        (child.startsWith("BAIB:") || child.startsWith("ScholarGPT:") || child.startsWith("VetGPT:"))
      ) {
        return (
          <span
            style={{
              color: "#5CFFB0", // always green
              fontWeight: 800,
              fontFamily: 'Fira Mono, monospace',
              letterSpacing: 0.5
            }}
          >
            {child}
          </span>
        );
      }
      return child;
    });
  };

  // Articles that should use the n8n-style layout (article-container, article-content, share-buttons)
  const articlesWithNewsStyleLayout = [
    'adcreative-ai',
    'activecampaign',
    'alli-ai',
    'apollo-io',
    'atria',
    'recomaze',
    'vida-ai-agent-os',
    'vista-social',
    'plesk',
    'surecam',
    'diginius',
    'smartli',
    'consensus',
    'browse-ai',
    'blackbox-ai',
    'beautiful-ai-complete-guide',
    'brevo-complete-guide',
    'capsule-crm-complete-guide',
    'close-complete-guide',
    'evolve-complete-guide',
    'alidrop-complete-guide',
    'flowith-io',
    'hume-ai',
    'practice-ignition',
    'omniseo',
    'compliancely',
    'teikametrics',
    'spiky-ai',
    'emergent',
    'sleekflow',
    'treble-ai',
    'invideo-ai',
    'lindy-ai',
    'lusha',
    'meetgeek-complete-guide',
    'miro-complete-guide',
    'mrpeasy-complete-guide',
    'murf-ai-complete-guide',
    'aisdr-complete-guide',
    'reply-io-complete-guide',
    'thordata',
    'tidio-ai',
    'veed-complete-guide',
    'webydo',
    'wispr-flow-complete-guide',
    'viral-launch',
    'runpod',
    'pipes-ai',
    'castmagic',
    'anybiz',
    'megahr',
    'bebop-ai',
    'catalister',
    'seosparkplug',
    'airia',
    'museit-art-complete-guide',
    'adwisely',
    'nanoz-complete-guide',
    'netlify-complete-guide',
    'prezi-complete-guide',
    'printify-complete-guide',
    'quillbot-complete-guide',
    'landingi-complete-guide',
    'tradify-complete-guide',
    'trainual-complete-guide',
    'travel-code-ai-complete-guide',
    'logome-complete-guide',
    'datahawk',
    'descript',
    'unbounce',
    'landbot',
    'reclaim-ai',
    'amplemarket',
    'mindstudio',
    'gamma',
    'rankprompt',
    'snowfire-ai',
    'volza',
    'bidx',
    'bitdefender-complete-guide',
    'rebolt-complete-guide',
    'calilio-complete-guide',
    'laxis',
    'krispcall',
    'getresponse',
    'gohighlevel',
    'weave',
    'turbotic',
    'creativescore',
    'databox',
    'instaglamor',
    'seamless-ai-complete-guide',
    'warmy-io-spam-folder-rebellion-email-deliverability',
    'pinecone-vector-database'
  ];
  const usesNewsStyleLayout = articlesWithNewsStyleLayout.includes(article.id);

  const renderShareButtons = () => {
    const url = `https://betteraibots.com/learn/${article.id}`;
    const text = `${article.title} - ${article.preview || ''}`;
    
    return (
      <div className="share-buttons" style={{ marginBottom: '30px' }}>
        <h3>Share this article:</h3>
        <div className="share-buttons-container">
          <button 
            onClick={() => {
              window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`, '_blank');
            }}
            className="share-btn twitter"
            title="Share on X"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
            Share to X
          </button>
          
          <button 
            onClick={() => {
              window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
            }}
            className="share-btn facebook"
            title="Share on Facebook"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
            Facebook
          </button>
          
          <button 
            onClick={() => {
              window.open(`https://reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(article.title)}`, '_blank');
            }}
            className="share-btn reddit"
            title="Share on Reddit"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
            </svg>
            Reddit
          </button>
          
          <button 
            onClick={() => {
              window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(text + ' ' + url)}`, '_blank');
            }}
            className="share-btn whatsapp"
            title="Share on WhatsApp"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
            </svg>
            WhatsApp
          </button>
          
          <button 
            onClick={() => {
              navigator.clipboard.writeText(url).then(() => {
                // Create and show quick "Copied" popup
                const popup = document.createElement('div');
                popup.textContent = 'Copied!';
                popup.style.cssText = `
                  position: fixed;
                  top: 20px;
                  right: 20px;
                  background: linear-gradient(135deg, #36ff95 0%, #00ffb2 100%);
                  color: #1a1a1a;
                  padding: 12px 20px;
                  border-radius: 8px;
                  font-weight: 600;
                  font-size: 14px;
                  z-index: 10000;
                  box-shadow: 0 4px 16px rgba(54, 255, 149, 0.3);
                  transform: translateX(100%);
                  transition: transform 0.3s ease;
                `;
                
                document.body.appendChild(popup);
                
                // Animate in
                setTimeout(() => {
                  popup.style.transform = 'translateX(0)';
                }, 10);
                
                // Remove after 2 seconds
                setTimeout(() => {
                  popup.style.transform = 'translateX(100%)';
                  setTimeout(() => {
                    if (document.body.contains(popup)) {
                      document.body.removeChild(popup);
                    }
                  }, 300);
                }, 2000);
              });
            }}
            className="share-btn copy"
            title="Copy link"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
            </svg>
            Copy Link
          </button>
        </div>
      </div>
    );
  };

  const topShareSection = usesNewsStyleLayout ? (
    renderShareButtons()
  ) : (
    <div style={{ display: "flex", gap: 16, marginBottom: 18, alignItems: "center" }}>
      <ShareButtons url={shareUrl} title={article.title} />
      {/* Open GPT Button for specific articles (top, small) */}
      {(() => {
        const gptMap = {
          "paintr": {
            name: "PaintR",
            link: "https://chatgpt.com/g/g-6833f9d3f1f88191ba83263ab7a99ea7-paintr"
          },
          "vetgpt": {
            name: "VetGPT",
            link: "https://chatgpt.com/g/g-ZR0aH16CQ-vetgpt"
          },
          "ai-fitness-coach": {
            name: "Fitness Diet Workout PhD Coach",
            link: "https://chatgpt.com/g/g-ipOIcM229-fitness-workout-diet-phd-coach"
          },
          "cheat-coder": {
            name: "Cheat Coder",
            link: "https://chatgpt.com/g/g-684426fa2a588191aabb529e9ca6e26f-cheat-coder"
          },
          "scholar-gpt": {
            name: "Scholar GPT",
            link: "https://chatgpt.com/g/g-kZ0eYXlJe-scholar-gpt"
          }
        };
        const gpt = gptMap[article.id];
        if (!gpt) return null;
        return (
          <a
            href={gpt.link}
            target="_blank"
            rel="noopener noreferrer"
            title="Open GPT"
            style={{
              width: 28,
              height: 28,
              minWidth: 28,
              minHeight: 28,
              borderRadius: 6,
              background: "linear-gradient(90deg, #36ff95 0%, #00ffb2 100%)",
              color: "#1a1a1a",
              textDecoration: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 800,
              fontSize: 13,
              verticalAlign: "middle",
              boxSizing: "border-box",
              marginTop: 18,
              marginBottom: 0
            }}
          >
            GPT
          </a>
        );
      })()}
    </div>
  );

  return (
    <>
      <Helmet>
        <title>{article.title} – BetterAiBots.com</title>
        <meta name="description" content={getArticleSeoDescription(article)} />
        <meta
          name="robots"
          content={
            isNoindexArticle
              ? "noindex, follow"
              : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
          }
        />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={getArticleSeoDescription(article)} />
        <link rel="canonical" href={`https://betteraibots.com/learn/${article.id}`} />
        <meta property="og:image" content={(() => {
          // Article-specific Open Graph images for better social sharing
          const ogImageMap = {
            "paintr": "https://betteraibots.com/paintindetifierai.webp",
            "invideo-ai": "https://betteraibots.com/assets/InVideoFreeTrial.png", 
            "scholar-gpt": "https://betteraibots.com/assets/bot-placeholder4.webp",
            "wispr-flow-complete-guide": "https://betteraibots.com/wispr.png",
            "cheat-coder": "https://betteraibots.com/assets/cheatcoder.png",
            "alli-ai": "https://betteraibots.com/alliai.png",
            "brevo-complete-guide": "https://betteraibots.com/brevologo.png",
            "capsule-crm-complete-guide": "https://betteraibots.com/capsulecrm.png",
            "close-complete-guide": "https://betteraibots.com/closeailogo.png",
            "evolve-complete-guide": "https://betteraibots.com/evolveai.jpg",
            "alidrop-complete-guide": "https://betteraibots.com/alidrop.png",
            "mrpeasy-complete-guide": "https://betteraibots.com/mrpeasy.png",
            "lindy-ai": "https://betteraibots.com/lindy.png",
            "viral-launch": "https://betteraibots.com/virallaunch.png",
            "apollo-io": "https://betteraibots.com/apollo.png",
            "atria": "https://betteraibots.com/assets/atria1.jpg",
            "beautiful-ai-complete-guide": "https://betteraibots.com/beautifulai.jpg",
            "recomaze": "https://betteraibots.com/assets/recomaze1.jpg",
            "vida-ai-agent-os": "https://betteraibots.com/assets/vida1.jpg",
            "vista-social": "https://betteraibots.com/assets/vistasocial.jpg",
            "plesk": "https://betteraibots.com/assets/plesk1.jpg",
            "surecam": "https://betteraibots.com/assets/surecam1.jpg",
            "diginius": "https://betteraibots.com/assets/diginius1.jpg",
            "smartli": "https://betteraibots.com/assets/smartli1.jpg",
            "consensus": "https://betteraibots.com/assets/consensus1.jpg",
            "browse-ai": "https://betteraibots.com/assets/browseai1.jpg",
            "adcreative-ai": "https://betteraibots.com/adcreative.png",
            "activecampaign": "https://betteraibots.com/activecampaign.webp",
            "flowith-io": "https://betteraibots.com/flowith.jpg",
            "murf-ai-complete-guide": "https://betteraibots.com/murfai.png",
            "aisdr-complete-guide": "https://betteraibots.com/aisdr.png",
            "miro-complete-guide": "https://betteraibots.com/miro.png",
            "blackbox-ai": "https://betteraibots.com/blackbox-logo.png?v=2",
            "webydo": "https://betteraibots.com/webydo-logo.png?v=2",
            "thordata": "https://betteraibots.com/thordata-logo.png?v=2",
            "seosparkplug": "https://betteraibots.com/assets/seosparkplug1.jpg",
            "adwisely": "https://betteraibots.com/adwisely.png",
            "catalister": "https://betteraibots.com/catalister2.jpg?v=2",
            "airia": "https://betteraibots.com/airiaaidemo.jpg?v=2",
            "runpod": "https://betteraibots.com/runpod.jpg?v=2",
            "pipes-ai": "https://betteraibots.com/pipesai.jpg?v=2",
            "castmagic": "https://betteraibots.com/castmagicai.jpg?v=2",
            "anybiz": "https://betteraibots.com/anybizio.jpg?v=2",
            "megahr": "https://betteraibots.com/megahr.jpg?v=2",
            "bebop-ai": "https://betteraibots.com/bebopaitools.jpg?v=2",
            "lusha": "https://betteraibots.com/lushaai.jpg?v=2",
            "meetgeek-complete-guide": "https://betteraibots.com/meetgeeklogo.png",
            "hume-ai": "https://betteraibots.com/hume2.jpg?v=2",
            "practice-ignition": "https://betteraibots.com/ignitionlogo.jpg",
            "omniseo": "https://betteraibots.com/omniseo.png",
            "compliancely": "https://betteraibots.com/compailogo.png",
            "teikametrics": "https://betteraibots.com/telkalogo.jpg",
            "spiky-ai": "https://betteraibots.com/spikyai.png",
            "emergent": "https://betteraibots.com/emergentai.png",
            "sleekflow": "https://betteraibots.com/sleekflowai.jpg",
            "treble-ai": "https://betteraibots.com/trebleai.jpg",
            "tidio-ai": "https://betteraibots.com/tidio2.jpg?v=2",
            "museit-art-complete-guide": "https://betteraibots.com/assets/musebrand.jpg",
            "nanoz-complete-guide": "https://betteraibots.com/assets/nanozlogo.png",
            "netlify-complete-guide": "https://betteraibots.com/netlifylogo.jpg",
            "pinecone-vector-database": "https://betteraibots.com/pineconeai.jpg",
            "prezi-complete-guide": "https://betteraibots.com/assets/preziai.png",
            "printify-complete-guide": "https://betteraibots.com/printifylogo.png",
            "quillbot-complete-guide": "https://betteraibots.com/assets/quilbot.jpg",
            "landingi-complete-guide": "https://betteraibots.com/assets/landingi1.jpg",
            "tradify-complete-guide": "https://betteraibots.com/assets/tradify1.jpg",
            "trainual-complete-guide": "https://betteraibots.com/trainualai.jpg",
            "logome-complete-guide": "https://betteraibots.com/assets/logome1.png",
            "datahawk": "https://betteraibots.com/assets/datahawkai.jpg",
            "descript": "https://betteraibots.com/assets/descriptai.png",
            "elevate-forward": "https://betteraibots.com/elevateforward.png",
            "unbounce": "https://betteraibots.com/assets/unbounceai.png",
            "landbot": "https://betteraibots.com/assets/landbotai.png",
            "reclaim-ai": "https://betteraibots.com/assets/reclaimai.png",
            "amplemarket": "https://betteraibots.com/amplemarketai.jpg",
            "mindstudio": "https://betteraibots.com/assets/mindstudioai.jpeg",
            "elevenlabs": "https://betteraibots.com/assets/elevenlabsai.jpg",
            "gamma": "https://betteraibots.com/assets/gammaai.jpg",
            "rankprompt": "https://betteraibots.com/assets/rankpromptai.jpg",
            "snowfire-ai": "https://betteraibots.com/assets/snowfireai.jpg",
            "volza": "https://betteraibots.com/assets/volzaai.png",
            "bidx": "https://betteraibots.com/assets/bidxai.jpg",
            "bitdefender-complete-guide": "https://betteraibots.com/bitdefenderlogo.png",
            "rebolt-complete-guide": "https://betteraibots.com/reboltai.jpg",
            "calilio-complete-guide": "https://betteraibots.com/caliliologo.webp",
            "laxis": "https://betteraibots.com/assets/laxisai.jpg",
            "krispcall": "https://betteraibots.com/assets/krispcallai.jpg",
            "getresponse": "https://betteraibots.com/assets/getresponseai.jpg",
            "gohighlevel": "https://betteraibots.com/gohighlevelai.jpg",
            "weave": "https://betteraibots.com/weaveai.jpg",
            "turbotic": "https://betteraibots.com/turbotic.png",
            "creativescore": "https://betteraibots.com/assets/creativescoreai.jpg",
            "databox": "https://betteraibots.com/assets/databoxai.jpg",
            "seamless-ai-complete-guide": "https://betteraibots.com/assets/seemlessailogo.jpg"
          };
          return ogImageMap[article.id] || primaryImage || article.cover;
        })()} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="article" />
        {article.id === "spiky-ai" && (
          <>
            <meta property="og:video" content="https://www.youtube.com/embed/YCmjayjco90" />
            <meta property="og:video:url" content="https://www.youtube.com/watch?v=YCmjayjco90" />
            <meta property="og:video:type" content="text/html" />
            <meta property="og:video:width" content="1280" />
            <meta property="og:video:height" content="720" />
            <meta property="og:video:duration" content="140" />
            <meta property="og:video:release_date" content="2025-03-24" />
            <meta property="video:tag" content="Spiky" />
            <meta property="video:tag" content="AI sales coaching" />
            <meta property="video:tag" content="meeting intelligence" />
          </>
        )}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={getArticleSeoDescription(article)} />
        <meta name="twitter:image" content={(() => {
          // Article-specific Open Graph images for better social sharing
          const ogImageMap = {
            "paintr": "https://betteraibots.com/paintindetifierai.webp",
            "invideo-ai": "https://betteraibots.com/assets/InVideoFreeTrial.png", 
            "scholar-gpt": "https://betteraibots.com/assets/bot-placeholder4.webp",
            "wispr-flow-complete-guide": "https://betteraibots.com/wispr.png",
            "cheat-coder": "https://betteraibots.com/assets/cheatcoder.png",
            "alli-ai": "https://betteraibots.com/alliai.png",
            "brevo-complete-guide": "https://betteraibots.com/brevologo.png",
            "capsule-crm-complete-guide": "https://betteraibots.com/capsulecrm.png",
            "close-complete-guide": "https://betteraibots.com/closeailogo.png",
            "evolve-complete-guide": "https://betteraibots.com/evolveai.jpg",
            "alidrop-complete-guide": "https://betteraibots.com/alidrop.png",
            "mrpeasy-complete-guide": "https://betteraibots.com/mrpeasy.png",
            "lindy-ai": "https://betteraibots.com/lindy.png",
            "viral-launch": "https://betteraibots.com/virallaunch.png",
            "apollo-io": "https://betteraibots.com/apollo.png",
            "atria": "https://betteraibots.com/assets/atria1.jpg",
            "beautiful-ai-complete-guide": "https://betteraibots.com/beautifulai.jpg",
            "recomaze": "https://betteraibots.com/assets/recomaze1.jpg",
            "vida-ai-agent-os": "https://betteraibots.com/assets/vida1.jpg",
            "vista-social": "https://betteraibots.com/assets/vistasocial.jpg",
            "plesk": "https://betteraibots.com/assets/plesk1.jpg",
            "surecam": "https://betteraibots.com/assets/surecam1.jpg",
            "diginius": "https://betteraibots.com/assets/diginius1.jpg",
            "smartli": "https://betteraibots.com/assets/smartli1.jpg",
            "consensus": "https://betteraibots.com/assets/consensus1.jpg",
            "browse-ai": "https://betteraibots.com/assets/browseai1.jpg",
            "adcreative-ai": "https://betteraibots.com/adcreative.png",
            "activecampaign": "https://betteraibots.com/activecampaign.webp",
            "flowith-io": "https://betteraibots.com/flowith.jpg",
            "murf-ai-complete-guide": "https://betteraibots.com/murfai.png",
            "aisdr-complete-guide": "https://betteraibots.com/aisdr.png",
            "miro-complete-guide": "https://betteraibots.com/miro.png",
            "blackbox-ai": "https://betteraibots.com/blackbox-logo.png?v=2",
            "webydo": "https://betteraibots.com/webydo-logo.png?v=2",
            "thordata": "https://betteraibots.com/thordata-logo.png?v=2",
            "seosparkplug": "https://betteraibots.com/assets/seosparkplug1.jpg",
            "adwisely": "https://betteraibots.com/adwisely.png",
            "catalister": "https://betteraibots.com/catalister2.jpg?v=2",
            "airia": "https://betteraibots.com/airiaaidemo.jpg?v=2",
            "runpod": "https://betteraibots.com/runpod.jpg?v=2",
            "pipes-ai": "https://betteraibots.com/pipesai.jpg?v=2",
            "castmagic": "https://betteraibots.com/castmagicai.jpg?v=2",
            "anybiz": "https://betteraibots.com/anybizio.jpg?v=2",
            "megahr": "https://betteraibots.com/megahr.jpg?v=2",
            "bebop-ai": "https://betteraibots.com/bebopaitools.jpg?v=2",
            "lusha": "https://betteraibots.com/lushaai.jpg?v=2",
            "meetgeek-complete-guide": "https://betteraibots.com/meetgeeklogo.png",
            "hume-ai": "https://betteraibots.com/hume2.jpg?v=2",
            "practice-ignition": "https://betteraibots.com/ignitionlogo.jpg",
            "omniseo": "https://betteraibots.com/omniseo.png",
            "compliancely": "https://betteraibots.com/compailogo.png",
            "teikametrics": "https://betteraibots.com/telkalogo.jpg",
            "spiky-ai": "https://betteraibots.com/spikyai.png",
            "emergent": "https://betteraibots.com/emergentai.png",
            "sleekflow": "https://betteraibots.com/sleekflowai.jpg",
            "treble-ai": "https://betteraibots.com/trebleai.jpg",
            "tidio-ai": "https://betteraibots.com/tidio2.jpg?v=2",
            "museit-art-complete-guide": "https://betteraibots.com/assets/musebrand.jpg",
            "nanoz-complete-guide": "https://betteraibots.com/assets/nanozlogo.png",
            "netlify-complete-guide": "https://betteraibots.com/netlifylogo.jpg",
            "pinecone-vector-database": "https://betteraibots.com/pineconeai.jpg",
            "prezi-complete-guide": "https://betteraibots.com/assets/preziai.png",
            "printify-complete-guide": "https://betteraibots.com/printifylogo.png",
            "quillbot-complete-guide": "https://betteraibots.com/assets/quilbot.jpg",
            "landingi-complete-guide": "https://betteraibots.com/assets/landingi1.jpg",
            "tradify-complete-guide": "https://betteraibots.com/assets/tradify1.jpg",
            "trainual-complete-guide": "https://betteraibots.com/trainualai.jpg",
            "logome-complete-guide": "https://betteraibots.com/assets/logome1.png",
            "datahawk": "https://betteraibots.com/assets/datahawkai.jpg",
            "descript": "https://betteraibots.com/assets/descriptai.png",
            "elevate-forward": "https://betteraibots.com/elevateforward.png",
            "unbounce": "https://betteraibots.com/assets/unbounceai.png",
            "landbot": "https://betteraibots.com/assets/landbotai.png",
            "reclaim-ai": "https://betteraibots.com/assets/reclaimai.png",
            "amplemarket": "https://betteraibots.com/amplemarketai.jpg",
            "mindstudio": "https://betteraibots.com/assets/mindstudioai.jpeg",
            "elevenlabs": "https://betteraibots.com/assets/elevenlabsai.jpg",
            "gamma": "https://betteraibots.com/assets/gammaai.jpg",
            "krispcall": "https://betteraibots.com/assets/krispcallai.jpg",
            "getresponse": "https://betteraibots.com/assets/getresponseai.jpg",
            "gohighlevel": "https://betteraibots.com/gohighlevelai.jpg",
            "weave": "https://betteraibots.com/weaveai.jpg",
            "turbotic": "https://betteraibots.com/turbotic.png"
          };
          return ogImageMap[article.id] || primaryImage || article.cover;
        })()} />
        <meta name="article:published_time" content={article.date} />
        <meta name="article:author" content="BetterAiBots" />
        <meta name="article:section" content="AI Tools" />
        <meta name="article:tag" content={getArticleKeywords(article.id)} />
        <meta name="keywords" content={getArticleKeywords(article.id)} />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": article.title,
            "description": getArticleSeoDescription(article),
            "image": (() => {
              // Article-specific Open Graph images for better social sharing
              const ogImageMap = {
                "paintr": "https://betteraibots.com/paintindetifierai.webp",
                "invideo-ai": "https://betteraibots.com/assets/InVideoFreeTrial.png", 
                "scholar-gpt": "https://betteraibots.com/assets/bot-placeholder4.webp",
                "wispr-flow-complete-guide": "https://betteraibots.com/wispr.png",
                "cheat-coder": "https://betteraibots.com/assets/cheatcoder.png",
                "alli-ai": "https://betteraibots.com/alliai.png",
                "brevo-complete-guide": "https://betteraibots.com/brevologo.png",
                "capsule-crm-complete-guide": "https://betteraibots.com/capsulecrm.png",
                "close-complete-guide": "https://betteraibots.com/closeailogo.png",
                "evolve-complete-guide": "https://betteraibots.com/evolveai.jpg",
                "alidrop-complete-guide": "https://betteraibots.com/alidrop.png",
                "mrpeasy-complete-guide": "https://betteraibots.com/mrpeasy.png",
                "lindy-ai": "https://betteraibots.com/lindy.png",
                "viral-launch": "https://betteraibots.com/virallaunch.png",
                "apollo-io": "https://betteraibots.com/apollo.png",
                "atria": "https://betteraibots.com/assets/atria1.jpg",
                "beautiful-ai-complete-guide": "https://betteraibots.com/beautifulai.jpg",
                "adcreative-ai": "https://betteraibots.com/adcreative.png",
                "activecampaign": "https://betteraibots.com/activecampaign.webp",
                "flowith-io": "https://betteraibots.com/flowith.jpg",
                "murf-ai-complete-guide": "https://betteraibots.com/murfai.png",
                "aisdr-complete-guide": "https://betteraibots.com/aisdr.png",
                "miro-complete-guide": "https://betteraibots.com/miro.png",
                "blackbox-ai": "https://betteraibots.com/blackbox-logo.png?v=2",
                "webydo": "https://betteraibots.com/webydo-logo.png?v=2",
                "thordata": "https://betteraibots.com/thordata-logo.png?v=2",
                "seosparkplug": "https://betteraibots.com/assets/seosparkplug1.jpg",
                "adwisely": "https://betteraibots.com/adwisely.png",
                "catalister": "https://betteraibots.com/catalister2.jpg?v=2",
                "airia": "https://betteraibots.com/airiaaidemo.jpg?v=2",
                "runpod": "https://betteraibots.com/runpod.jpg?v=2",
                "pipes-ai": "https://betteraibots.com/pipesai.jpg?v=2",
                "castmagic": "https://betteraibots.com/castmagicai.jpg?v=2",
                "anybiz": "https://betteraibots.com/anybizio.jpg?v=2",
                "megahr": "https://betteraibots.com/megahr.jpg?v=2",
                "bebop-ai": "https://betteraibots.com/bebopaitools.jpg?v=2",
                "museit-art-complete-guide": "https://betteraibots.com/assets/musebrand.jpg",
            "nanoz-complete-guide": "https://betteraibots.com/assets/nanozlogo.png",
            "netlify-complete-guide": "https://betteraibots.com/netlifylogo.jpg",
            "pinecone-vector-database": "https://betteraibots.com/pineconeai.jpg",
            "prezi-complete-guide": "https://betteraibots.com/assets/preziai.png",
            "printify-complete-guide": "https://betteraibots.com/printifylogo.png",
                "quillbot-complete-guide": "https://betteraibots.com/assets/quilbot.jpg",
                "landingi-complete-guide": "https://betteraibots.com/assets/landingi1.jpg",
                "vista-social": "https://betteraibots.com/assets/vistasocial.jpg",
                "datahawk": "https://betteraibots.com/assets/datahawkai.jpg",
                "descript": "https://betteraibots.com/assets/descriptai.png",
                "elevate-forward": "https://betteraibots.com/elevateforward.png",
                "unbounce": "https://betteraibots.com/assets/unbounceai.png",
                "landbot": "https://betteraibots.com/assets/landbotai.png",
                "treble-ai": "https://betteraibots.com/trebleai.jpg",
                "reclaim-ai": "https://betteraibots.com/assets/reclaimai.png",
                "amplemarket": "https://betteraibots.com/amplemarketai.jpg",
                "mindstudio": "https://betteraibots.com/assets/mindstudioai.jpeg",
                "elevenlabs": "https://betteraibots.com/assets/elevenlabsai.jpg",
                "gamma": "https://betteraibots.com/assets/gammaai.jpg",
                "rankprompt": "https://betteraibots.com/assets/rankpromptai.jpg",
                "snowfire-ai": "https://betteraibots.com/assets/snowfireai.jpg",
                "volza": "https://betteraibots.com/assets/volzaai.png",
            "bidx": "https://betteraibots.com/assets/bidxai.jpg",
            "bitdefender-complete-guide": "https://betteraibots.com/bitdefenderlogo.png",
            "rebolt-complete-guide": "https://betteraibots.com/reboltai.jpg",
            "calilio-complete-guide": "https://betteraibots.com/caliliologo.webp",
            "laxis": "https://betteraibots.com/assets/laxisai.jpg",
            "krispcall": "https://betteraibots.com/assets/krispcallai.jpg",
            "getresponse": "https://betteraibots.com/assets/getresponseai.jpg",
            "gohighlevel": "https://betteraibots.com/gohighlevelai.jpg",
            "weave": "https://betteraibots.com/weaveai.jpg",
            "turbotic": "https://betteraibots.com/turbotic.png",
            "creativescore": "https://betteraibots.com/assets/creativescoreai.jpg",
            "databox": "https://betteraibots.com/assets/databoxai.jpg",
            "seamless-ai-complete-guide": "https://betteraibots.com/assets/seemlessailogo.jpg",
            "practice-ignition": "https://betteraibots.com/ignitionlogo.jpg",
            "omniseo": "https://betteraibots.com/omniseo.png",
            "compliancely": "https://betteraibots.com/compailogo.png",
            "spiky-ai": "https://betteraibots.com/spikyai.png",
            "emergent": "https://betteraibots.com/emergentai.png",
            "sleekflow": "https://betteraibots.com/sleekflowai.jpg"
          };
              return ogImageMap[article.id] || primaryImage || article.cover;
            })(),
            "author": {
              "@type": "Organization",
              "name": "BetterAiBots",
              "url": "https://betteraibots.com"
            },
            "publisher": {
              "@type": "Organization",
              "name": "BetterAiBots",
              "logo": {
                "@type": "ImageObject",
                "url": "https://betteraibots.com/betteraibotsglowlogo8.png"
              }
            },
            "datePublished": article.date,
            "dateModified": article.date,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": pageUrl
            },
            "keywords": getArticleKeywords(article.id),
            "articleSection": "AI Tools",
            "inLanguage": "en-US"
          })}
        </script>
        {readMoreFaqSchema && (
          <script type="application/ld+json">
            {JSON.stringify(readMoreFaqSchema)}
          </script>
        )}
        <script type="application/ld+json">
          {JSON.stringify(articleBreadcrumbSchema)}
        </script>
        {article.id === "spiky-ai" && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "VideoObject",
              "name": "Spiky Platform Demo | Unlock Sales Potential with AI-Driven Insights",
              "description": "See how Spiky.ai delivers real-time AI sales coaching, call momentum and objection handling, playbook execution (e.g. MEDDPICC), and pipeline visibility so your team scales winning behaviors and closes more deals.",
              "thumbnailUrl": "https://img.youtube.com/vi/YCmjayjco90/maxresdefault.jpg",
              "uploadDate": "2025-03-24",
              "duration": "PT2M20S",
              "embedUrl": "https://www.youtube.com/embed/YCmjayjco90",
              "contentUrl": "https://www.youtube.com/watch?v=YCmjayjco90",
              "publisher": {
                "@type": "Organization",
                "name": "BetterAiBots",
                "logo": {
                  "@type": "ImageObject",
                  "url": "https://betteraibots.com/betteraibotsglowlogo8.png"
                }
              }
            })}
          </script>
        )}
      </Helmet>
      
      <div className="article-container">
      {topShareSection}
      <div className="article-content">
      {/* Top Image */}
      {(primaryImage || article.id === "instaglamor") && (
        article.id === "instaglamor" ? (
          <a
            href="https://instaglamor.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "block",
              cursor: "pointer",
              textDecoration: "none"
            }}
          >
            <img src={primaryImage} alt="" style={{
              maxWidth: "600px",
              width: "auto",
              height: "auto",
              borderRadius: 16,
              margin: "0 0 24px 0",
              display: "block",
              objectFit: "contain",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 4px 20px rgba(54, 255, 149, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "none";
            }}
            />
          </a>
        ) : article.id === "seosparkplug" ? (
          <a 
            href="https://seosparkplug.com" 
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "block",
              cursor: "pointer",
              textDecoration: "none"
            }}
          >
            <img src={primaryImage} alt="" style={{
              maxWidth: "600px",
              width: "auto",
              height: "auto",
              borderRadius: 16,
              margin: "0 0 24px 0",
              display: "block",
              objectFit: "contain",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }} 
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 4px 20px rgba(54, 255, 149, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "none";
            }}
            />
          </a>
        ) : article.id === "adwisely" ? (
          <a 
            href="https://get.adwisely.com/BAIB" 
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "block",
              cursor: "pointer",
              textDecoration: "none"
            }}
          >
            <img src={primaryImage} alt="" style={{
              maxWidth: "600px",
              width: "auto",
              height: "auto",
              borderRadius: 16,
              margin: "0 0 24px 0",
              display: "block",
              objectFit: "contain",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }} 
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 4px 20px rgba(54, 255, 149, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "none";
            }}
            />
          </a>
        ) : article.id === "runpod" ? (
          <a 
            href="https://get.runpod.io/w3na2cm4xdjp" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              display: "block",
              cursor: "pointer",
              textDecoration: "none"
            }}
          >
            <img src={primaryImage} alt="" style={{
              maxWidth: "600px",
              width: "auto",
              height: "auto",
              borderRadius: 16,
              margin: "0 0 24px 0",
              display: "block",
              objectFit: "contain",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }} 
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 4px 20px rgba(54, 255, 149, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "none";
            }}
            />
          </a>
        ) : article.id === "catalister" ? (
          <a 
            href="https://join.catalister.com/BAIB" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              display: "block",
              cursor: "pointer",
              textDecoration: "none"
            }}
          >
            <img src={primaryImage} alt="" style={{
              maxWidth: "600px",
              width: "auto",
              height: "auto",
              borderRadius: 16,
              margin: "0 0 24px 0",
              display: "block",
              objectFit: "contain",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }} 
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 4px 20px rgba(54, 255, 149, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "none";
            }}
            />
          </a>
        ) : article.id === "airia" ? (
          <a 
            href="https://try.airia.com/BAIB" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              display: "block",
              cursor: "pointer",
              textDecoration: "none"
            }}
          >
            <img src={primaryImage} alt="" style={{
              maxWidth: "600px",
              width: "auto",
              height: "auto",
              borderRadius: 16,
              margin: "0 0 24px 0",
              display: "block",
              objectFit: "contain",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }} 
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 4px 20px rgba(54, 255, 149, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "none";
            }}
            />
          </a>
        ) : article.id === "runpod" ? (
          <a 
            href="https://get.runpod.io/w3na2cm4xdjp" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              display: "block",
              cursor: "pointer",
              textDecoration: "none"
            }}
          >
            <img src={primaryImage} alt="" style={{
              maxWidth: "600px",
              width: "auto",
              height: "auto",
              borderRadius: 16,
              margin: "0 0 24px 0",
              display: "block",
              objectFit: "contain",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }} 
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 4px 20px rgba(54, 255, 149, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "none";
            }}
            />
          </a>
        ) : article.id === "pipes-ai" ? (
          <a 
            href="https://try.pipes.ai/hmqj0m3am6un" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              display: "block",
              cursor: "pointer",
              textDecoration: "none"
            }}
          >
            <img src={primaryImage} alt="" style={{
              maxWidth: "600px",
              width: "auto",
              height: "auto",
              borderRadius: 16,
              margin: "0 0 24px 0",
              display: "block",
              objectFit: "contain",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }} 
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 4px 20px rgba(54, 255, 149, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "none";
            }}
            />
          </a>
        ) : article.id === "castmagic" ? (
          <a 
            href="https://get.castmagic.io/qdu0jfhfcurv" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              display: "block",
              cursor: "pointer",
              textDecoration: "none"
            }}
          >
            <img src={primaryImage} alt="" style={{
              maxWidth: "600px",
              width: "auto",
              height: "auto",
              borderRadius: 16,
              margin: "0 0 24px 0",
              display: "block",
              objectFit: "contain",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }} 
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 4px 20px rgba(54, 255, 149, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "none";
            }}
            />
          </a>
        ) : article.id === "anybiz" ? (
          <a 
            href="https://anybiz.io/?fpr=ai4n56" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              display: "block",
              cursor: "pointer",
              textDecoration: "none"
            }}
          >
            <img src={primaryImage} alt="" style={{
              maxWidth: "600px",
              width: "auto",
              height: "auto",
              borderRadius: 16,
              margin: "0 0 24px 0",
              display: "block",
              objectFit: "contain",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }} 
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 4px 20px rgba(54, 255, 149, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "none";
            }}
            />
          </a>
        ) : article.id === "megahr" ? (
          <a 
            href="https://try.megahr.com/BAIB" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              display: "block",
              cursor: "pointer",
              textDecoration: "none"
            }}
          >
            <img src={primaryImage} alt="" style={{
              maxWidth: "600px",
              width: "auto",
              height: "auto",
              borderRadius: 16,
              margin: "0 0 24px 0",
              display: "block",
              objectFit: "contain",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }} 
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 4px 20px rgba(54, 255, 149, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "none";
            }}
            />
          </a>
        ) : article.id === "bebop-ai" ? (
          <a 
            href="https://try.bebop.ai/o004cood3aod" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              display: "block",
              cursor: "pointer",
              textDecoration: "none"
            }}
          >
            <img src={primaryImage} alt="" style={{
              maxWidth: "600px",
              width: "auto",
              height: "auto",
              borderRadius: 16,
              margin: "0 0 24px 0",
              display: "block",
              objectFit: "contain",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }} 
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 4px 20px rgba(54, 255, 149, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "none";
            }}
            />
          </a>
        ) : article.id === "lusha" ? (
          <a 
            href="https://partnerstack.lusha.com/w61xn76pa3sr" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              display: "block",
              cursor: "pointer",
              textDecoration: "none"
            }}
          >
            <img src={primaryImage} alt="" style={{
              maxWidth: "600px",
              width: "auto",
              height: "auto",
              borderRadius: 16,
              margin: "0 0 24px 0",
              display: "block",
              objectFit: "contain",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }} 
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 4px 20px rgba(54, 255, 149, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "none";
            }}
            />
          </a>
        ) : article.id === "hume-ai" ? (
          <a 
            href="https://try.hume.ai/BAIB" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              display: "block",
              cursor: "pointer",
              textDecoration: "none"
            }}
          >
            <img src={primaryImage} alt="" style={{
              maxWidth: "600px",
              width: "auto",
              height: "auto",
              borderRadius: 16,
              margin: "0 0 24px 0",
              display: "block",
              objectFit: "contain",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }} 
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 4px 20px rgba(54, 255, 149, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "none";
            }}
            />
          </a>
        ) : article.id === "tidio-ai" ? (
          <a 
            href="https://affiliate.tidio.com/BAIB" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              display: "block",
              cursor: "pointer",
              textDecoration: "none"
            }}
          >
            <img src={primaryImage} alt="" style={{
              maxWidth: "600px",
              width: "auto",
              height: "auto",
              borderRadius: 16,
              margin: "0 0 24px 0",
              display: "block",
              objectFit: "contain",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }} 
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 4px 20px rgba(54, 255, 149, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "none";
            }}
            />
          </a>
        ) : article.id === "prezi-complete-guide" ? (
          <a 
            href="https://try.prezi.com/9kk83fjh4yri" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              display: "block",
              cursor: "pointer",
              textDecoration: "none"
            }}
          >
            <img src={primaryImage} alt="" style={{
              maxWidth: "600px",
              width: "auto",
              height: "auto",
              borderRadius: 16,
              margin: "0 0 24px 0",
              display: "block",
              objectFit: "contain",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }} 
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 4px 20px rgba(54, 255, 149, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "none";
            }}
            />
          </a>
        ) : article.id === "quillbot-complete-guide" ? (
          <a 
            href="https://try.quillbot.com/BAIB" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              display: "block",
              cursor: "pointer",
              textDecoration: "none"
            }}
          >
            <img src={primaryImage} alt="" style={{
              maxWidth: "600px",
              width: "auto",
              height: "auto",
              borderRadius: 16,
              margin: "0 0 24px 0",
              display: "block",
              objectFit: "contain",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }} 
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 4px 20px rgba(54, 255, 149, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "none";
            }}
            />
          </a>
        ) : article.id === "nanoz-complete-guide" ? (
          <a 
            href="https://nanoz.fun" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              display: "block",
              cursor: "pointer",
              textDecoration: "none"
            }}
          >
            <img src={primaryImage} alt="" style={{
              maxWidth: "600px",
              width: "auto",
              height: "auto",
              borderRadius: 16,
              margin: "0 0 24px 0",
              display: "block",
              objectFit: "contain",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }} 
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 4px 20px rgba(54, 255, 149, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "none";
            }}
            />
          </a>
        ) : article.id === "landingi-complete-guide" ? (
          <a 
            href="https://try.landingi.com/lwfc597yjm25" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              display: "block",
              cursor: "pointer",
              textDecoration: "none"
            }}
          >
            <img src={primaryImage} alt="" style={{
              maxWidth: "600px",
              width: "auto",
              height: "auto",
              borderRadius: 16,
              margin: "0 0 24px 0",
              display: "block",
              objectFit: "contain",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }} 
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 4px 20px rgba(54, 255, 149, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "none";
            }}
            />
          </a>
        ) : article.id === "tradify-complete-guide" ? (
          <a 
            href="https://partners.tradifyhq.com/rh188vnbjrvr" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              display: "block",
              cursor: "pointer",
              textDecoration: "none"
            }}
          >
            <img src={primaryImage} alt="" style={{
              maxWidth: "600px",
              width: "auto",
              height: "auto",
              borderRadius: 16,
              margin: "0 0 24px 0",
              display: "block",
              objectFit: "contain",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }} 
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 4px 20px rgba(54, 255, 149, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "none";
            }}
            />
          </a>
        ) : article.id === "rankprompt" ? (
          <a 
            href="https://join.rankprompt.com/BAIB" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              display: "block",
              cursor: "pointer",
              textDecoration: "none"
            }}
          >
            <img src={primaryImage} alt="" style={{
              maxWidth: "600px",
              width: "auto",
              height: "auto",
              borderRadius: 16,
              margin: "0 0 24px 0",
              display: "block",
              objectFit: "contain",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }} 
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 4px 20px rgba(54, 255, 149, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "none";
            }}
            />
          </a>
        ) : article.id === "snowfire-ai" ? (
          <a 
            href="https://partners.snowfire.ai/8f5vtlj0mksq" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              display: "block",
              cursor: "pointer",
              textDecoration: "none"
            }}
          >
            <img src={primaryImage} alt="" style={{
              maxWidth: "600px",
              width: "auto",
              height: "auto",
              borderRadius: 16,
              margin: "0 0 24px 0",
              display: "block",
              objectFit: "contain",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }} 
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 4px 20px rgba(54, 255, 149, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "none";
            }}
            />
          </a>
        ) : article.id === "volza" ? (
          <a 
            href="https://partner.volza.com/BAIB" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              display: "block",
              cursor: "pointer",
              textDecoration: "none"
            }}
          >
            <img src={primaryImage} alt="" style={{
              maxWidth: "600px",
              width: "auto",
              height: "auto",
              borderRadius: 16,
              margin: "0 0 24px 0",
              display: "block",
              objectFit: "contain",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }} 
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 4px 20px rgba(54, 255, 149, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "none";
            }}
            />
          </a>
        ) : article.id === "logome-complete-guide" ? (
          <a 
            href="https://logomeai.partnerlinks.io/BAIB" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              display: "block",
              cursor: "pointer",
              textDecoration: "none"
            }}
          >
            <img src={primaryImage} alt="" style={{
              maxWidth: "600px",
              width: "auto",
              height: "auto",
              borderRadius: 16,
              margin: "0 0 24px 0",
              display: "block",
              objectFit: "contain",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }} 
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 4px 20px rgba(54, 255, 149, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "none";
            }}
            />
          </a>
        ) : article.id === "bidx" ? (
          <a 
            href="https://try.bidx.io/BAIB" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              display: "block",
              cursor: "pointer",
              textDecoration: "none"
            }}
          >
            <img src={primaryImage} alt="" style={{
              maxWidth: "600px",
              width: "auto",
              height: "auto",
              borderRadius: 16,
              margin: "0 0 24px 0",
              display: "block",
              objectFit: "contain",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }} 
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 4px 20px rgba(54, 255, 149, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "none";
            }}
            />
          </a>
        ) : article.id === "laxis" ? (
          <a 
            href="https://get.laxis.com/BAIB" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              display: "block",
              cursor: "pointer",
              textDecoration: "none"
            }}
          >
            <img src={primaryImage} alt="" style={{
              maxWidth: "600px",
              width: "auto",
              height: "auto",
              borderRadius: 16,
              margin: "0 0 24px 0",
              display: "block",
              objectFit: "contain",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }} 
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 4px 20px rgba(54, 255, 149, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "none";
            }}
            />
          </a>
        ) : article.id === "krispcall" ? (
          <a 
            href="https://try.krispcall.com/BAIB" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              display: "block",
              cursor: "pointer",
              textDecoration: "none"
            }}
          >
            <img src={primaryImage} alt="" style={{
              maxWidth: "600px",
              width: "auto",
              height: "auto",
              borderRadius: 16,
              margin: "0 0 24px 0",
              display: "block",
              objectFit: "contain",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }} 
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 4px 20px rgba(54, 255, 149, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "none";
            }}
            />
          </a>
        ) : article.id === "activecampaign" ? (
          <a 
            href="https://try.activecampaign.com/q84v43xjanzq" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              display: "block",
              cursor: "pointer",
              textDecoration: "none"
            }}
          >
            <img src={primaryImage} alt="" style={{
              maxWidth: "600px",
              width: "auto",
              height: "auto",
              borderRadius: 16,
              margin: "0 0 24px 0",
              display: "block",
              objectFit: "contain",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }} 
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 4px 20px rgba(54, 255, 149, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "none";
            }}
            />
          </a>
        ) : article.id === "getresponse" ? (
          <a 
            href="https://try.getresponsetoday.com/BAIB" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              display: "block",
              cursor: "pointer",
              textDecoration: "none"
            }}
          >
            <img src={primaryImage} alt="" style={{
              maxWidth: "600px",
              width: "auto",
              height: "auto",
              borderRadius: 16,
              margin: "0 0 24px 0",
              display: "block",
              objectFit: "contain",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }} 
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 4px 20px rgba(54, 255, 149, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "none";
            }}
            />
          </a>
        ) : article.id === "creativescore" ? (
          <a 
            href="https://affiliate.creativescore.ai/BAIB" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              display: "block",
              cursor: "pointer",
              textDecoration: "none"
            }}
          >
            <img src={primaryImage} alt="" style={{
              maxWidth: "600px",
              width: "auto",
              height: "auto",
              borderRadius: 16,
              margin: "0 0 24px 0",
              display: "block",
              objectFit: "contain",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }} 
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 4px 20px rgba(54, 255, 149, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "none";
            }}
            />
          </a>
        ) : article.id === "databox" ? (
          <a 
            href="https://join.databox.com/BAIB" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              display: "block",
              cursor: "pointer",
              textDecoration: "none"
            }}
          >
            <img src={primaryImage} alt="" style={{
              maxWidth: "600px",
              width: "auto",
              height: "auto",
              borderRadius: 16,
              margin: "0 0 24px 0",
              display: "block",
              objectFit: "contain",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }} 
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 4px 20px rgba(54, 255, 149, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "none";
            }}
            />
          </a>
        ) : article.id === "descript" ? (
          <a 
            href="https://get.descript.com/BAIB" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              display: "block",
              cursor: "pointer",
              textDecoration: "none"
            }}
          >
            <img src={primaryImage} alt="" style={{
              maxWidth: "600px",
              width: "auto",
              height: "auto",
              borderRadius: 16,
              margin: "0 0 24px 0",
              display: "block",
              objectFit: "contain",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }} 
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 4px 20px rgba(54, 255, 149, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "none";
            }}
            />
          </a>
        ) : article.id === "elevate-forward" ? (
          <a 
            href="https://partners.elevateforward.ai/iy1986n6gbz4" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              display: "block",
              cursor: "pointer",
              textDecoration: "none"
            }}
          >
            <img src={primaryImage} alt="" style={{
              maxWidth: "600px",
              width: "auto",
              height: "auto",
              borderRadius: 16,
              margin: "0 0 24px 0",
              display: "block",
              objectFit: "contain",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }} 
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 4px 20px rgba(54, 255, 149, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "none";
            }}
            />
          </a>
        ) : article.id === "unbounce" ? (
          <a 
            href="https://unbounce.partnerlinks.io/BAIB" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              display: "block",
              cursor: "pointer",
              textDecoration: "none"
            }}
          >
            <img src={primaryImage} alt="" style={{
              maxWidth: "600px",
              width: "auto",
              height: "auto",
              borderRadius: 16,
              margin: "0 0 24px 0",
              display: "block",
              objectFit: "contain",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }} 
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 4px 20px rgba(54, 255, 149, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "none";
            }}
            />
          </a>
        ) : article.id === "treble-ai" ? (
          <a 
            href="https://trebleai.partnerlinks.io/91f6vx0zwova" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              display: "block",
              cursor: "pointer",
              textDecoration: "none"
            }}
          >
            <img src={primaryImage} alt="" style={{
              maxWidth: "600px",
              width: "auto",
              height: "auto",
              borderRadius: 16,
              margin: "0 0 24px 0",
              display: "block",
              objectFit: "contain",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }} 
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 4px 20px rgba(54, 255, 149, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "none";
            }}
            />
          </a>
        ) : article.id === "landbot" ? (
          <a 
            href="https://get.landbotlab.com/BAIB" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              display: "block",
              cursor: "pointer",
              textDecoration: "none"
            }}
          >
            <img src={primaryImage} alt="" style={{
              maxWidth: "600px",
              width: "auto",
              height: "auto",
              borderRadius: 16,
              margin: "0 0 24px 0",
              display: "block",
              objectFit: "contain",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }} 
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 4px 20px rgba(54, 255, 149, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "none";
            }}
            />
          </a>
        ) : article.id === "reclaim-ai" ? (
          <a 
            href="https://go.reclaim.ai/BAIB" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              display: "block",
              cursor: "pointer",
              textDecoration: "none"
            }}
          >
            <img src={primaryImage} alt="" style={{
              maxWidth: "600px",
              width: "auto",
              height: "auto",
              borderRadius: 16,
              margin: "0 0 24px 0",
              display: "block",
              objectFit: "contain",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }} 
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 4px 20px rgba(54, 255, 149, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "none";
            }}
            />
          </a>
        ) : article.id === "amplemarket" ? (
          <a 
            href="https://grow.amplemarket.com/BAIB" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              display: "block",
              cursor: "pointer",
              textDecoration: "none"
            }}
          >
            <img src={primaryImage} alt="" style={{
              maxWidth: "600px",
              width: "auto",
              height: "auto",
              borderRadius: 16,
              margin: "0 0 24px 0",
              display: "block",
              objectFit: "contain",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }} 
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 4px 20px rgba(54, 255, 149, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "none";
            }}
            />
          </a>
        ) : article.id === "mindstudio" ? (
          <a 
            href="https://get.mindstudio.ai/BAIB" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              display: "block",
              cursor: "pointer",
              textDecoration: "none"
            }}
          >
            <img src={primaryImage} alt="" style={{
              maxWidth: "600px",
              width: "auto",
              height: "auto",
              borderRadius: 16,
              margin: "0 0 24px 0",
              display: "block",
              objectFit: "contain",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }} 
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 4px 20px rgba(54, 255, 149, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "none";
            }}
            />
          </a>
        ) : article.id === "seamless-ai-complete-guide" ? (
          <a 
            href="https://get.seamless.ai/BAIB" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              display: "block",
              cursor: "pointer",
              textDecoration: "none"
            }}
          >
            <img src={primaryImage} alt="" style={{
              maxWidth: "600px",
              width: "auto",
              height: "auto",
              borderRadius: 16,
              margin: "0 0 24px 0",
              display: "block",
              objectFit: "contain",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }} 
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.05)";
              e.target.style.boxShadow = "0 4px 20px rgba(54, 255, 149, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.boxShadow = "none";
            }}
            />
          </a>
        ) : (
          <img src={primaryImage} alt="" style={{
            maxWidth: "600px",
            width: "auto",
            height: "auto",
            borderRadius: 16,
            margin: "0 0 24px 0",
            display: "block",
            objectFit: "contain"
          }} />
        )
      )}
      <h1 className="article-page-title">{article.title}</h1>
      {article.id === 'amplemarket' && (
        <ArticleGuideVideo
          src={amplemarketUserGuideVideo}
          ariaLabel="Amplemarket user guide"
          affiliateLink={affiliateLink}
          linkLabel="Visit Amplemarket"
        />
      )}
      {article.id === 'close-complete-guide' && (
        <ArticleGuideVideo
          src={closeAIUserGuideVideo}
          ariaLabel="Close CRM user guide"
          affiliateLink={affiliateLink}
          linkLabel="Visit Close"
        />
      )}
      {article.id === 'bitdefender-complete-guide' && (
        <ArticleGuideVideo
          src={bitdefenderUserGuideVideo}
          ariaLabel="Bitdefender user guide"
          affiliateLink={affiliateLink}
          linkLabel="Get Bitdefender"
        />
      )}
      {article.id === 'adcreative-ai' && (
        <div style={{ textAlign: 'center', margin: '0 auto 28px', maxWidth: '800px', width: '100%' }}>
          <div
            style={{
              position: 'relative',
              width: '100%',
              aspectRatio: '16 / 9',
              borderRadius: 16,
              overflow: 'hidden',
              background: '#0d1520',
              boxShadow: '0 4px 20px rgba(54, 255, 149, 0.2)',
            }}
          >
            <video
              src="/AdCreative.ai Promo.mp4"
              aria-label="AdCreative.ai Promo"
              controls
              playsInline
              preload="metadata"
              style={{
                width: '100%',
                height: '100%',
                display: 'block',
                objectFit: 'contain',
                background: '#000',
              }}
            />
          </div>
          <a
            href={affiliateLink}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-block',
              marginTop: '16px',
              color: 'var(--accent)',
              fontWeight: 700,
              fontSize: '1.1rem',
              textDecoration: 'underline',
            }}
          >
            Start free trial
          </a>
        </div>
      )}
      {video && (
        <div dangerouslySetInnerHTML={{ __html: video }} />
      )}
      {article.id === 'capsule-crm-complete-guide' && (
        <ArticleGuideVideo
          src={capsuleUserGuideVideo}
          ariaLabel="Capsule CRM user guide"
          affiliateLink={affiliateLink}
          linkLabel="Try Capsule CRM Free"
        />
      )}
      {typeof articleMarkdownContent === "string" ? (
        <>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={{
            code: ({ node, children, ...props }) => {
              // Don't render code blocks that contain video HTML
              const codeContent = typeof children === 'string' ? children : String(children);
              if (codeContent.includes('youtube.com/embed') || codeContent.includes('<iframe')) {
                return null; // Don't render video HTML as code
              }
              return <code {...props}>{children}</code>;
            },
            pre: ({ node, children, ...props }) => {
              // Don't render pre blocks that contain video HTML
              const preContent = typeof children === 'string' ? children : String(children);
              if (preContent.includes('youtube.com/embed') || preContent.includes('<iframe')) {
                return null; // Don't render video HTML as code
              }
              return <pre {...props}>{children}</pre>;
            },
            div: ({ node, children, ...props }) => {
              // Check if this div contains an iframe (video)
              if (node?.properties?.dangerouslySetInnerHTML || 
                  (typeof children === 'string' && children.includes('iframe'))) {
                return <div {...props} dangerouslySetInnerHTML={{ __html: children }} />;
              }
              return <div {...props}>{children}</div>;
            },
            h2: ScholarGPTHeading,
            h3: ({ node, children, ...props }) => {
              // Extract text content from children, handling both strings and React elements
              const extractTextFromChildren = (children) => {
                if (typeof children === 'string') {
                  return children;
                }
                if (Array.isArray(children)) {
                  return children.map(child => {
                    if (typeof child === 'string') {
                      return child;
                    }
                    if (child && typeof child === 'object' && child.props && child.props.children) {
                      return extractTextFromChildren(child.props.children);
                    }
                    return '';
                  }).join('');
                }
                if (children && typeof children === 'object' && children.props && children.props.children) {
                  return extractTextFromChildren(children.props.children);
                }
                return '';
              };
              
              const headingText = extractTextFromChildren(children);
              const headingId = generateHeadingId(headingText);
              return (
                <h3 {...props} id={headingId} className="article-md-heading" style={{ margin: '28px 0 10px 0', fontSize: '1.13rem', letterSpacing: 0.1 }}>{children}</h3>
              );
            },
            p: ({ children }) => <p>{children}</p>,
            strong: ({ children, ...props }) => {
              // Neon highlight for speaker names
              return highlightSpeakers(children);
            },
            ul: ({ children, ...props }) => (
              <ul style={{ paddingLeft: 20, margin: '16px 0' }}>{children}</ul>
            ),
            ol: ({ children, ...props }) => (
              <ol style={{ paddingLeft: 20, margin: '16px 0' }}>{children}</ol>
            ),
            li: ({ children, ...props }) => (
              <li style={{ marginBottom: 8, display: 'block' }}>{children}</li>
            ),
            img: ({ src, alt, ...props }) => {
              // Allow images that are part of article content (not user-submitted)
              // Wrap Recomaze and Vida images in affiliate links
              if (src && (src.includes('recomaze2') || src.includes('recomaze3'))) {
                return (
                  <div style={{ textAlign: 'center', margin: '30px 0' }}>
                    <a
                      href="https://affiliate.recomaze.ai/BAIB"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'inline-block', cursor: 'pointer' }}
                    >
                      <img
                        src={src}
                        alt={alt || ''}
                        style={{
                          maxWidth: '100%',
                          height: 'auto',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          display: 'block'
                        }}
                        {...props}
                      />
                    </a>
                  </div>
                );
              }
              if (src && (src.includes('vidaai2') || src.includes('vida2'))) {
                return (
                  <div style={{ textAlign: 'center', margin: '30px 0' }}>
                    <a
                      href="https://try.vida.io/BAIB"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'inline-block', cursor: 'pointer' }}
                    >
                      <img
                        src={src}
                        alt={alt || ''}
                        style={{
                          maxWidth: '100%',
                          height: 'auto',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          display: 'block'
                        }}
                        {...props}
                      />
                    </a>
                  </div>
                );
              }
              if (src && (src.includes('plesk2') || src.includes('plesk'))) {
                return (
                  <div style={{ textAlign: 'center', margin: '30px 0' }}>
                    <a
                      href="https://try.plesk.com/BAIB"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'inline-block', cursor: 'pointer' }}
                    >
                      <img
                        src={src}
                        alt={alt || ''}
                        style={{
                          maxWidth: '100%',
                          height: 'auto',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          display: 'block'
                        }}
                        {...props}
                      />
                    </a>
                  </div>
                );
              }
              if (src && (src.includes('surecam2') || src.includes('surecam'))) {
                return (
                  <div style={{ textAlign: 'center', margin: '30px 0' }}>
                    <a
                      href="https://join.surecam.com/BAIB"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'inline-block', cursor: 'pointer' }}
                    >
                      <img
                        src={src}
                        alt={alt || ''}
                        style={{
                          maxWidth: '100%',
                          height: 'auto',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          display: 'block'
                        }}
                        {...props}
                      />
                    </a>
                  </div>
                );
              }
              if (src && (src.includes('diginius2') || src.includes('diginius'))) {
                return (
                  <div style={{ textAlign: 'center', margin: '30px 0' }}>
                    <a
                      href="https://get.diginius.com/rvao67pwqqux"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'inline-block', cursor: 'pointer' }}
                    >
                      <img
                        src={src}
                        alt={alt || ''}
                        style={{
                          maxWidth: '100%',
                          height: 'auto',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          display: 'block'
                        }}
                        {...props}
                      />
                    </a>
                  </div>
                );
              }
              if (src && (src.includes('smartli2') || src.includes('smartli'))) {
                return (
                  <div style={{ textAlign: 'center', margin: '30px 0' }}>
                    <a
                      href="https://smartli.partnerlinks.io/BAIB"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'inline-block', cursor: 'pointer' }}
                    >
                      <img
                        src={src}
                        alt={alt || ''}
                        style={{
                          maxWidth: '100%',
                          height: 'auto',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          display: 'block'
                        }}
                        {...props}
                      />
                    </a>
                  </div>
                );
              }
              if (src && (src.includes('consensus2') || src.includes('consensus'))) {
                return (
                  <div style={{ textAlign: 'center', margin: '30px 0' }}>
                    <a
                      href="https://get.consensus.app/BAIB"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'inline-block', cursor: 'pointer' }}
                    >
                      <img
                        src={src}
                        alt={alt || ''}
                        style={{
                          maxWidth: '100%',
                          height: 'auto',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          display: 'block'
                        }}
                        {...props}
                      />
                    </a>
                  </div>
                );
              }
              if (src && (src.includes('browseai2') || src.includes('browseai'))) {
                return (
                  <div style={{ textAlign: 'center', margin: '30px 0' }}>
                    <a
                      href="https://partners.browse.ai/BAIB"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'inline-block', cursor: 'pointer' }}
                    >
                      <img
                        src={src}
                        alt={alt || ''}
                        style={{
                          maxWidth: '100%',
                          height: 'auto',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          display: 'block'
                        }}
                        {...props}
                      />
                    </a>
                  </div>
                );
              }
              if (src && (src.includes('prezi2') || src.includes('preziai') || src.includes('prezi'))) {
                return (
                  <div style={{ textAlign: 'center', margin: '30px 0' }}>
                    <a
                      href="https://try.prezi.com/9kk83fjh4yri"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'inline-block', cursor: 'pointer' }}
                    >
                      <img
                        src={src}
                        alt={alt || ''}
                        style={{
                          maxWidth: '100%',
                          height: 'auto',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          display: 'block'
                        }}
                        {...props}
                      />
                    </a>
                  </div>
                );
              }
              if (src && (src.includes('landingi2') || src.includes('landingi1') || src.includes('landingi'))) {
                return (
                  <div style={{ textAlign: 'center', margin: '30px 0' }}>
                    <a
                      href="https://try.landingi.com/lwfc597yjm25"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'inline-block', cursor: 'pointer' }}
                    >
                      <img
                        src={src}
                        alt={alt || ''}
                        style={{
                          maxWidth: '100%',
                          height: 'auto',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          display: 'block'
                        }}
                        {...props}
                      />
                    </a>
                  </div>
                );
              }
              // Default image rendering
              return (
                <img
                  src={src}
                  alt={alt || ''}
                  style={{
                    maxWidth: '100%',
                    height: 'auto',
                    margin: '30px 0',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'block'
                  }}
                  {...props}
                />
              );
            },
            a: ({ href, children, ...props }) => {
              // Handle anchor links for TOC first
              if (href && href.startsWith('#')) {
                return (
                  <a
                    href={href}
                    onClick={(e) => {
                      e.preventDefault();
                      const targetId = href.substring(1);
                      const targetElement = document.getElementById(targetId);
                      if (targetElement) {
                        targetElement.scrollIntoView({ 
                          behavior: 'auto',
                          block: 'start'
                        });
                      }
                    }}
                    style={{ color: 'var(--accent)', textDecoration: 'underline', cursor: 'pointer' }}
                    {...props}
                  >
                    {children}
                  </a>
                );
              }
              
              // Handle internal article links (/learn/ routes)
              if (href && (href.startsWith('/learn/') || href.startsWith('https://betteraibots.com/learn/'))) {
                const articleId = href.replace(/^.*\/learn\//, '').replace(/\/$/, '');
                return (
                  <Link
                    to={`/learn/${articleId}`}
                    state={{ from: fromPage }}
                    onClick={() => window.scrollTo(0, 0)}
                    style={{ 
                      color: 'var(--accent)', 
                      textDecoration: 'underline',
                      cursor: 'pointer',
                    }}
                    {...props}
                  >
                    {children}
                  </Link>
                );
              }
              
              // Check if this is the InVideo link
              if (href && href.includes('invideo.sjv.io')) {
                return (
                  <div style={{ textAlign: 'center', margin: '40px 0' }}>
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-block',
                        background: 'linear-gradient(90deg, #36ff95 0%, #00ffb2 100%)',
                        color: '#1a1a1a',
                        padding: '16px 32px',
                        borderRadius: '12px',
                        textDecoration: 'none',
                        fontWeight: '700',
                        fontSize: '1.2rem',
                        boxShadow: '0 4px 16px rgba(54, 255, 149, 0.3)',
                        transition: 'all 0.3s ease',
                        border: 'none',
                        cursor: 'pointer'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 6px 20px rgba(54, 255, 149, 0.4)';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 16px rgba(54, 255, 149, 0.3)';
                      }}
                    >
                      {children}
                    </a>
                  </div>
                );
              }
              // Apollo.io CTA button with container
              if (href && href.includes('get.apollo.io')) {
                return (
                  <div className="article-cta-panel">
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-block',
                        background: 'linear-gradient(90deg, #36ff95 0%, #00ffb2 100%)',
                        color: '#1a1a1a',
                        padding: '16px 32px',
                        borderRadius: '12px',
                        textDecoration: 'none',
                        fontWeight: 700,
                        fontSize: '1.2rem',
                        boxShadow: '0 4px 16px rgba(54, 255, 149, 0.3)'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 6px 20px rgba(54, 255, 149, 0.4)';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 16px rgba(54, 255, 149, 0.3)';
                      }}
                    >
                      {children && typeof children[0] === 'string' ? children : 'Try Apollo Now →'}
                    </a>
                  </div>
                );
              }
              // Lindy.ai CTA button with container
              if (href && href.includes('try.lindy.ai')) {
                return (
                  <div className="article-cta-panel">
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-block',
                        background: 'linear-gradient(90deg, #36ff95 0%, #00ffb2 100%)',
                        color: '#1a1a1a',
                        padding: '16px 32px',
                        borderRadius: '12px',
                        textDecoration: 'none',
                        fontWeight: 700,
                        fontSize: '1.2rem',
                        boxShadow: '0 4px 16px rgba(54, 255, 149, 0.3)'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 6px 20px rgba(54, 255, 149, 0.4)';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 16px rgba(54, 255, 149, 0.3)';
                      }}
                    >
                      {children && typeof children[0] === 'string' ? children : 'Try Lindy.ai Now →'}
                    </a>
                  </div>
                );
              }
              // Viral Launch CTA button with container
              if (href && href.includes('affiliate.viral-launch.com')) {
                return (
                  <div className="article-cta-panel">
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-block',
                        background: 'linear-gradient(90deg, #36ff95 0%, #00ffb2 100%)',
                        color: '#1a1a1a',
                        padding: '16px 32px',
                        borderRadius: '12px',
                        textDecoration: 'none',
                        fontWeight: 700,
                        fontSize: '1.2rem',
                        boxShadow: '0 4px 16px rgba(54, 255, 149, 0.3)'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 6px 20px rgba(54, 255, 149, 0.4)';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 16px rgba(54, 255, 149, 0.3)';
                      }}
                    >
                      {children && typeof children[0] === 'string' ? children : 'Try Viral Launch Now →'}
                    </a>
                  </div>
                );
              }
              // Flowith CTA button with container
              if (href && href.includes('aff.flowith.io')) {
                return (
                  <div className="article-cta-panel">
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-block',
                        background: 'linear-gradient(90deg, #36ff95 0%, #00ffb2 100%)',
                        color: '#1a1a1a',
                        padding: '16px 32px',
                        borderRadius: '12px',
                        textDecoration: 'none',
                        fontWeight: 700,
                        fontSize: '1.2rem',
                        boxShadow: '0 4px 16px rgba(54, 255, 149, 0.3)'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 6px 20px rgba(54, 255, 149, 0.4)';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 16px rgba(54, 255, 149, 0.3)';
                      }}
                    >
                      {children && typeof children[0] === 'string' ? children : 'Try Flowith Now →'}
                    </a>
                  </div>
                );
              }
              // AdCreative CTA button with container
              if (href && href.includes('adcreative.ai')) {
                return (
                  <div className="article-cta-panel">
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-block',
                        background: 'linear-gradient(90deg, #36ff95 0%, #00ffb2 100%)',
                        color: '#1a1a1a',
                        padding: '16px 32px',
                        borderRadius: '12px',
                        textDecoration: 'none',
                        fontWeight: 700,
                        fontSize: '1.2rem',
                        boxShadow: '0 4px 16px rgba(54, 255, 149, 0.3)'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 6px 20px rgba(54, 255, 149, 0.4)';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 16px rgba(54, 255, 149, 0.3)';
                      }}
                    >
                      {children && typeof children[0] === 'string' ? children : 'Try AdCreative Now →'}
                    </a>
                  </div>
                );
              }
              // Share button styling
              if (href && (href.includes('twitter.com/intent/tweet') || href.includes('facebook.com/sharer') || href.includes('linkedin.com/sharing') || href.includes('mailto:') || href === '#' || (children && (children.toString().includes('Share on X') || children.toString().includes('Share on Facebook') || children.toString().includes('Share on LinkedIn') || children.toString().includes('Share via Email') || children.toString().includes('Copy Link'))))) {
                return (
                  <button
                    onClick={() => {
                      if (href.includes('mailto:')) {
                        window.location.href = href;
                      } else if (href === '#') {
                        // Copy link functionality with quick popup
                        // Always copy the article URL, not affiliate links
                        const articleUrl = `${window.location.origin}/learn/${window.location.pathname.split('/').pop()}`;
                        navigator.clipboard.writeText(articleUrl).then(() => {
                          // Create and show quick "Copied" popup
                          const popup = document.createElement('div');
                          popup.textContent = 'Copied!';
                          popup.style.cssText = `
                            position: fixed;
                            top: 20px;
                            right: 20px;
                            background: linear-gradient(135deg, #36ff95 0%, #00ffb2 100%);
                            color: #1a1a1a;
                            padding: 12px 20px;
                            border-radius: 8px;
                            font-weight: 600;
                            font-size: 14px;
                            z-index: 10000;
                            box-shadow: 0 4px 16px rgba(54, 255, 149, 0.3);
                            transform: translateX(100%);
                            transition: transform 0.3s ease;
                          `;
                          
                          document.body.appendChild(popup);
                          
                          // Animate in
                          setTimeout(() => {
                            popup.style.transform = 'translateX(0)';
                          }, 10);
                          
                          // Remove after 2 seconds
                          setTimeout(() => {
                            popup.style.transform = 'translateX(100%)';
                            setTimeout(() => {
                              if (document.body.contains(popup)) {
                                document.body.removeChild(popup);
                              }
                            }, 300);
                          }, 2000);
                        });
                      } else {
                        window.open(href, '_blank');
                      }
                    }}
                    style={{
                      display: 'inline-block',
                      background: 'linear-gradient(135deg, #2a3a4a 0%, #1a2330 100%)',
                      color: '#36ff95',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      textDecoration: 'none',
                      fontWeight: '600',
                      fontSize: '0.9rem',
                      border: '1px solid #36ff9522',
                      margin: '4px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.background = 'linear-gradient(135deg, #36ff95 0%, #00ffb2 100%)';
                      e.target.style.color = '#1a1a1a';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.background = 'linear-gradient(135deg, #2a3a4a 0%, #1a2330 100%)';
                      e.target.style.color = '#36ff95';
                    }}
                  >
                    {children}
                  </button>
                );
              }
              // Handle image links (affiliate links that contain images from markdown)
              // ReactMarkdown renders [![alt](img)](link) as <a><img /></a>
              // Check if this is an affiliate link and if children contains an img element
              if (href && (href.includes('affiliates.tryatria.com') || href.includes('affiliate.recomaze.ai') || href.includes('try.vida.io') || href.includes('try.plesk.com') || href.includes('join.surecam.com') || href.includes('get.diginius.com') || href.includes('smartli.partnerlinks.io') || href.includes('try.prezi.com') || href.includes('try.landingi.com') || href.includes('nanoz.fun') || href.includes('partners.tradifyhq.com') || href.includes('logomeai.partnerlinks.io'))) {
                try {
                  // Check if children contains an image element - use simple, safe checking
                  let hasImage = false;
                  if (children) {
                    // Handle single child that is an img
                    if (typeof children === 'object' && !Array.isArray(children) && children.type === 'img') {
                      hasImage = true;
                    }
                    // Handle array of children - check if any is an img
                    else if (Array.isArray(children)) {
                      hasImage = children.some(child => 
                        child && typeof child === 'object' && !Array.isArray(child) && child.type === 'img'
                      );
                    }
                  }
                  
                  if (hasImage) {
                    return (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'block',
                          textAlign: 'center',
                          margin: '30px 0',
                          cursor: 'pointer'
                        }}
                        {...props}
                      >
                        {children}
                      </a>
                    );
                  }
                } catch (e) {
                  // If check fails, fall through to default link styling
                  console.error("Error checking for image in link children:", e);
                }
              }
              // Default link styling
              return (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--accent)', textDecoration: 'underline' }}
                  {...props}
                >
                  {children}
                </a>
              );
            },
            table: ({ children, ...props }) => (
              <div style={{ overflowX: 'auto', margin: '20px 0' }}>
                <table {...props} style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  border: '1px solid #36ff9522',
                  borderRadius: '8px',
                  overflow: 'hidden'
                }}>
                  {children}
                </table>
              </div>
            ),
            thead: ({ children, ...props }) => (
              <thead {...props} className="article-table-head">
                {children}
              </thead>
            ),
            tbody: ({ children, ...props }) => (
              <tbody {...props}>
                {children}
              </tbody>
            ),
            tr: ({ children, ...props }) => (
              <tr {...props} style={{ borderBottom: '1px solid #36ff9522' }}>
                {children}
              </tr>
            ),
            th: ({ children, ...props }) => (
              <th {...props} className="article-table-th">
                {children}
              </th>
            ),
            td: ({ children, ...props }) => (
              <td {...props} className="article-table-td">
                {children}
              </td>
            ),
          }}
        >
          {secondaryImages.length > 0 ? firstHalf : articleMarkdownContent}
        </ReactMarkdown>
        {secondaryImages.length > 0 && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
            margin: '40px 0',
            alignItems: 'center'
          }}>
            {secondaryImages.map((imgSrc, idx) => {
              const isVideo = imgSrc && (imgSrc.endsWith('.mp4') || imgSrc.endsWith('.webm') || imgSrc.endsWith('.mov'));
              
              if (isVideo) {
                return affiliateLink ? (
                  <a
                    key={idx}
                    href={affiliateLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'block',
                      cursor: 'pointer',
                      textDecoration: 'none'
                    }}
                  >
                    <video 
                      src={imgSrc}
                      controls
                      style={{
                        width: "100%",
                        maxWidth: "600px",
                        height: "auto",
                        borderRadius: 16,
                        display: "block",
                        boxShadow: "0 4px 20px rgba(54, 255, 149, 0.2)",
                        transition: "transform 0.2s ease, box-shadow 0.2s ease"
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = "scale(1.02)";
                        e.target.style.boxShadow = "0 6px 24px rgba(54, 255, 149, 0.3)";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = "scale(1)";
                        e.target.style.boxShadow = "0 4px 20px rgba(54, 255, 149, 0.2)";
                      }}
                    />
                  </a>
                ) : (
                  <video 
                    key={idx}
                    src={imgSrc}
                    controls
                    style={{
                      width: "100%",
                      maxWidth: "600px",
                      height: "auto",
                      borderRadius: 16,
                      display: "block",
                      boxShadow: "0 4px 20px rgba(54, 255, 149, 0.2)"
                    }}
                  />
                );
              }
              
              return affiliateLink ? (
                <a
                  key={idx}
                  href={affiliateLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'block',
                    cursor: 'pointer',
                    textDecoration: 'none'
                  }}
                >
                  <img 
                    src={imgSrc} 
                    alt="" 
                    style={{
                      width: "100%",
                      maxWidth: "600px",
                      height: "auto",
                      borderRadius: 16,
                      display: "block",
                      objectFit: "cover",
                      boxShadow: "0 4px 20px rgba(54, 255, 149, 0.2)",
                      transition: "transform 0.2s ease, box-shadow 0.2s ease"
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = "scale(1.02)";
                      e.target.style.boxShadow = "0 6px 24px rgba(54, 255, 149, 0.3)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = "scale(1)";
                      e.target.style.boxShadow = "0 4px 20px rgba(54, 255, 149, 0.2)";
                    }}
                  />
                </a>
              ) : (
                <img 
                  key={idx}
                  src={imgSrc} 
                  alt="" 
                  style={{
                    width: "100%",
                    maxWidth: "600px",
                    height: "auto",
                    borderRadius: 16,
                    display: "block",
                    objectFit: "cover",
                    boxShadow: "0 4px 20px rgba(54, 255, 149, 0.2)"
                  }} 
                />
              );
            })}
          </div>
        )}
        {secondaryImages.length > 0 && secondHalf && (
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={markdownComponents}
          >
            {secondHalf}
          </ReactMarkdown>
        )}
        </>
      ) : (
        articleMarkdownContent
      )}
      {/* Middle image before interview section */}
      {article.id === 'vetgpt' && images[1] && (
        <div style={{ textAlign: "center", margin: "38px 0 14px 0" }}>
          <img src={images[1]} alt="" style={{
            width: "70%",
            maxWidth: 370,
            borderRadius: 18,
            boxShadow: "0 0 16px #36ff9577"
          }} />
        </div>
      )}
      {/* Lower image at very bottom */}
      {images[2] && (
        <div style={{ textAlign: "center", margin: "38px 0 14px 0" }}>
          <img src={images[2]} alt="" style={{
            width: "70%",
            maxWidth: 370,
            borderRadius: 18,
            boxShadow: "0 0 16px #36ff9577"
          }} />
        </div>
      )}
      {article.id === 'adcreative-ai' && (
        <div style={{ textAlign: 'center', margin: '48px auto 24px', maxWidth: '600px' }}>
          <video
            src="/SQUAREad.mp4"
            aria-label="AdCreative"
            autoPlay
            loop
            muted
            playsInline
            style={{
              width: '100%',
              maxWidth: '600px',
              height: 'auto',
              borderRadius: 16,
              display: 'block',
              margin: '0 auto',
              boxShadow: '0 4px 20px rgba(54, 255, 149, 0.2)',
            }}
          />
          <a
            href={affiliateLink}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-block',
              marginTop: '16px',
              color: 'var(--accent)',
              fontWeight: 700,
              fontSize: '1.1rem',
              textDecoration: 'underline',
            }}
          >
            Start free trial
          </a>
        </div>
      )}
      {/* Scholar GPT image and button at bottom */}
      {article.id === 'scholar-gpt' && (
        <>
          <div style={{ textAlign: "center", margin: "38px 0 16px 0" }}>
            <img
              src="/scholargpt.jpg"
              alt="Scholar GPT"
              style={{
                width: '100%',
                maxWidth: '420px',
                display: 'block',
                margin: '0 auto',
                borderRadius: 16
              }}
            />
          </div>
          <div style={{ textAlign: "center", margin: "16px 0 0 0" }}>
            <a
              href="https://chatgpt.com/g/g-kZ0eYXlJe-scholar-gpt"
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: "none" }}
            >
              <button className="openai-btn-standalone">Open Scholar GPT on OpenAI</button>
            </a>
          </div>
        </>
      )}
      {/* Open GPT Button for other articles (bottom, large) */}
      {article.id !== 'scholar-gpt' && (() => {
        const gptMap = {
          "paintr": {
            name: "PaintR",
            link: "https://chatgpt.com/g/g-6833f9d3f1f88191ba83263ab7a99ea7-paintr"
          },
          "vetgpt": {
            name: "VetGPT",
            link: "https://chatgpt.com/g/g-ZR0aH16CQ-vetgpt"
          },
          "ai-fitness-coach": {
            name: "Fitness Diet Workout PhD Coach",
            link: "https://chatgpt.com/g/g-ipOIcM229-fitness-workout-diet-phd-coach"
          },
          "cheat-coder": {
            name: "Cheat Coder",
            link: "https://chatgpt.com/g/g-684426fa2a588191aabb529e9ca6e26f-cheat-coder"
          }
        };
        const gpt = gptMap[article.id];
        if (!gpt) return null;
        return (
          <div style={{ textAlign: "center", margin: "38px 0 0 0" }}>
            <a
              href={gpt.link}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: "none" }}
            >
              <button className="openai-btn-standalone">{`Open ${gpt.name} on OpenAI`}</button>
            </a>
          </div>
        );
      })()}
      </div>
      {/* Bottom-centered share buttons */}
      {usesNewsStyleLayout ? (
        <div className="article-footer">
          {renderShareButtons()}
        </div>
      ) : (
        <div style={{ display: "flex", justifyContent: "center", margin: "32px 0 0 0" }}>
          <ShareButtons url={shareUrl} title={article.title} />
        </div>
      )}
      
      {/* Related Articles Section */}
      {(() => {
        const relatedArticles = findRelatedArticles(article, articles, 2);
        if (relatedArticles.length === 0) return null;
        
        return (
          <div className="article-related-section">
            <h2 className="article-related-section-title">
              Related Articles
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: '20px'
            }}>
              {relatedArticles.map((relatedArticle) => {
                // Use cover image directly (same as primaryImage - webpack processes it automatically)
                const relatedCover = relatedArticle.cover || null;
                
                return (
                  <Link
                    key={relatedArticle.id}
                    to={`/learn/${relatedArticle.id}`}
                    state={{ from: fromPage }}
                    onClick={() => window.scrollTo(0, 0)}
                    className="article-related-card"
                  >
                    {relatedCover && (
                      <div className="article-related-card__img-wrap">
                        <img 
                          src={relatedCover} 
                          alt={relatedArticle.title}
                          style={{
                            width: '200px',
                            height: '200px',
                            maxWidth: '200px',
                            maxHeight: '200px',
                            objectFit: 'cover',
                            borderRadius: '8px',
                            transition: 'transform 0.3s ease'
                          }}
                          onError={(e) => {
                            // Fallback if image fails to load
                            console.error('Failed to load related article image:', relatedCover, 'for article:', relatedArticle.id);
                            e.target.style.display = 'none';
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'scale(1.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'scale(1)';
                          }}
                        />
                      </div>
                    )}
                    <div style={{ padding: '20px' }}>
                      <h3 className="article-related-card__title">
                        {relatedArticle.title}
                      </h3>
                      {relatedArticle.preview && (
                        <p className="article-related-card__preview" style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          marginBottom: '10px'
                        }}>
                          {relatedArticle.preview}
                        </p>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Explore more - internal links for indexing */}
      <nav className="article-explore-nav" aria-label="Explore more">
        <h2>Explore more</h2>
        <p>Discover AI tools, guides, and news on BetterAiBots.</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
          <Link to="/learn">Learn AI guides</Link>
          <span style={{ color: 'var(--text-subtle)' }}> · </span>
          <Link to="/news">News</Link>
          <span style={{ color: 'var(--text-subtle)' }}> · </span>
          <Link to="/apps">Apps</Link>
          <span style={{ color: 'var(--text-subtle)' }}> · </span>
          <Link to="/">Home</Link>
        </div>
      </nav>
      </div>
    </>
  );
}
